import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiGrid, FiEdit2, FiPlus, FiX, FiCheck, FiTrash2, FiRefreshCw, FiUpload, FiImage, FiSave, FiChevronDown, FiFileText, FiInfo, FiEdit, FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import FazyraTouchForm from '../components/FazyraTouchForm';
import EventHighlightsForm from '../components/EventHighlightsForm';
import HelpYouPlanForm from '../components/HelpYouPlanForm';
import DynamicSectionsForm from '../components/DynamicSectionsForm';
import EventPreview from '../components/EventPreview';

const API_URL = process.env.REACT_APP_API_URL || '/api';

// Security utility functions
const sanitizeInput = (text, maxLength = 500) => {
  if (!text) return '';
  return text
    .toString()
    .trim()
    .replace(/<[^>]*>/g, '') // Remove HTML tags
    .substring(0, maxLength);
};

const validateImageFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  const maxSize = 10 * 1024 * 1024; // 10MB
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only image files (JPEG, PNG, GIF, WebP) are allowed');
  }
  
  if (file.size > maxSize) {
    throw new Error('Image file size must be less than 10MB');
  }
  
  return true;
};

const validateVideoFile = (file) => {
  const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/webm'];
  const maxSize = 100 * 1024 * 1024; // 100MB
  
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only video files (MP4, MOV, AVI, WebM) are allowed');
  }
  
  if (file.size > maxSize) {
    throw new Error('Video file size must be less than 100MB');
  }
  
  return true;
};

const HomePageManagement = () => {
  const { token, user } = useAuth();
  const [message, setMessage] = useState({ text: '', type: '' });
  const [activeTab, setActiveTab] = useState('eventTypes'); // 'eventTypes', 'description', 'promiseSections', 'heroVideos', or 'decoSlides'
  
  // Homepage Description state
  const [homepageDescription, setHomepageDescription] = useState('');
  const [descriptionLoading, setDescriptionLoading] = useState(false);
  const [descriptionSaving, setDescriptionSaving] = useState(false);
  const MAX_DESCRIPTION_CHARS = 500;
  
  // Promise Sections state
  const [promiseSections, setPromiseSections] = useState([]);
  const [promiseSectionsLoading, setPromiseSectionsLoading] = useState(false);
  const [showPromiseModal, setShowPromiseModal] = useState(false);
  const [editingPromise, setEditingPromise] = useState(null);
  const [promiseForm, setPromiseForm] = useState({
    title: '',
    description: '',
    videoUrl: '',
    layout: 'text-left',
    order: 0,
    isActive: true
  });
  const [promiseVideoFile, setPromiseVideoFile] = useState(null);
  const [promiseSaving, setPromiseSaving] = useState(false);
  
  // Hero Videos state
  const [heroVideos, setHeroVideos] = useState([]);
  const [heroVideosLoading, setHeroVideosLoading] = useState(false);
  const [showHeroVideoModal, setShowHeroVideoModal] = useState(false);
  const [editingHeroVideo, setEditingHeroVideo] = useState(null);
  const [heroVideoForm, setHeroVideoForm] = useState({
    title: '',
    videoUrl: '',
    order: 0,
    isActive: true
  });
  const [heroVideoFile, setHeroVideoFile] = useState(null);
  const [heroVideoSaving, setHeroVideoSaving] = useState(false);
  
  // Deco Slides state
  const [decoSlides, setDecoSlides] = useState([]);
  const [decoSlidesLoading, setDecoSlidesLoading] = useState(false);
  const [showDecoSlideModal, setShowDecoSlideModal] = useState(false);
  const [editingDecoSlide, setEditingDecoSlide] = useState(null);
  const [decoSlideForm, setDecoSlideForm] = useState({
    title: '',
    description: '',
    imageUrl: '',
    order: 0,
    isActive: true
  });
  const [decoSlideImageFile, setDecoSlideImageFile] = useState(null);
  const [decoSlideImagePreview, setDecoSlideImagePreview] = useState(null);
  const [decoSlideSaving, setDecoSlideSaving] = useState(false);
  
  // Catering Cards state
  const [cateringCards, setCateringCards] = useState([]);
  const [cateringCardsLoading, setCateringCardsLoading] = useState(false);
  const [showCateringCardModal, setShowCateringCardModal] = useState(false);
  const [editingCateringCard, setEditingCateringCard] = useState(null);
  const [cateringCardForm, setCateringCardForm] = useState({
    name: '',
    description: '',
    videoUrl: '',
    title: '',
    subtitle: '',
    order: 0,
    isActive: true
  });
  const [cateringCardVideoFile, setCateringCardVideoFile] = useState(null);
  const [cateringCardVideoPreview, setCateringCardVideoPreview] = useState(null);
  const [cateringCardSaving, setCateringCardSaving] = useState(false);
  
  // Event Types management state
  const [eventTypes, setEventTypes] = useState([]);
  const [eventTypesLoading, setEventTypesLoading] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    posterImage: '',
    order: 0,
    isActive: true,
    fazyraTouch: {
      enabled: false,
      image1: '',
      image1Alt: '',
      image1Preview: null,
      image2: '',
      image2Alt: '',
      image2Preview: null,
      image3: '',
      image3Alt: '',
      image3Preview: null,
      title: 'The Fazyra Touch',
      subtitle: 'Where Every Detail Tells Your Story',
      mainDescription: '',
      features: [
        { label: '', description: '' },
        { label: '', description: '' },
        { label: '', description: '' }
      ],
      closingDescription: ''
    },
    highlights: {
      enabled: false,
      title: 'Event Highlights',
      subtitle: 'Creating Moments That Last Forever',
      cards: []
    },
    helpYouPlan: {
      enabled: false,
      sectionHeading: 'What We Can Help You Plan',
      cards: []
    },
    dynamicSections: []
  });
  const [eventImageFile, setEventImageFile] = useState(null);
  const [eventImagePreview, setEventImagePreview] = useState(null);
  const [fazyraTouchImageFiles, setFazyraTouchImageFiles] = useState({
    image1: null,
    image2: null,
    image3: null
  });
  const [helpYouPlanVideoFiles, setHelpYouPlanVideoFiles] = useState({});
  const [helpYouPlanVideoPreviews, setHelpYouPlanVideoPreviews] = useState({});
  const [dynamicSectionImageFiles, setDynamicSectionImageFiles] = useState({});
  const [eventSaving, setEventSaving] = useState(false);
  
  // Preview mode states
  const [previewMode, setPreviewMode] = useState(false);
  const [editingSection, setEditingSection] = useState(null); // 'basic', 'fazyraTouch', 'highlights', 'helpYouPlan'
  const [showAddSectionMenu, setShowAddSectionMenu] = useState(false);

  // Show message
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => {
      setMessage({ text: '', type: '' });
    }, 5000);
  };

  // Load homepage description
  const loadHomepageDescription = async () => {
    setDescriptionLoading(true);
    try {
      const response = await fetch(`${API_URL}/homepage`);
      const data = await response.json();
      
      if (data.success) {
        setHomepageDescription(data.data.description || '');
      } else {
        showMessage('Failed to load homepage description', 'error');
      }
    } catch (error) {
      console.error('Error fetching homepage description:', error);
      showMessage('Error loading homepage description', 'error');
    } finally {
      setDescriptionLoading(false);
    }
  };

  // Save homepage description
  const saveHomepageDescription = async () => {
    if (!homepageDescription.trim()) {
      showMessage('Description cannot be empty', 'error');
      return;
    }

    if (homepageDescription.length > MAX_DESCRIPTION_CHARS) {
      showMessage(`Description exceeds maximum length of ${MAX_DESCRIPTION_CHARS} characters`, 'error');
      return;
    }

    setDescriptionSaving(true);
    try {
      const response = await fetch(`${API_URL}/homepage`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ description: homepageDescription })
      });

      const data = await response.json();

      if (data.success) {
        showMessage('Homepage description updated successfully!', 'success');
      } else {
        showMessage(data.message || 'Failed to update description', 'error');
      }
    } catch (error) {
      console.error('Error saving homepage description:', error);
      showMessage('Failed to save changes. Please try again.', 'error');
    } finally {
      setDescriptionSaving(false);
    }
  };

  // Load all promise sections
  const loadPromiseSections = async () => {
    setPromiseSectionsLoading(true);
    try {
      const response = await fetch(`${API_URL}/promise-sections/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setPromiseSections(result.data);
      } else {
        showMessage('Failed to load promise sections', 'error');
      }
    } catch (error) {
      console.error('Error loading promise sections:', error);
      showMessage('Error loading promise sections', 'error');
    } finally {
      setPromiseSectionsLoading(false);
    }
  };

  // Open modal for creating new promise section
  const openCreatePromiseModal = () => {
    setEditingPromise(null);
    setPromiseForm({
      title: '',
      description: '',
      videoUrl: '',
      layout: 'text-left',
      order: promiseSections.length + 1,
      isActive: true
    });
    setPromiseVideoFile(null);
    setShowPromiseModal(true);
  };

  // Open modal for editing promise section
  const openEditPromiseModal = (section) => {
    setEditingPromise(section);
    setPromiseForm({
      title: section.title,
      description: section.description,
      videoUrl: section.videoUrl,
      layout: section.layout,
      order: section.order,
      isActive: section.isActive
    });
    setPromiseVideoFile(null);
    setShowPromiseModal(true);
  };

  // Close promise modal
  const closePromiseModal = () => {
    setShowPromiseModal(false);
    setEditingPromise(null);
    setPromiseForm({
      title: '',
      description: '',
      videoUrl: '',
      layout: 'text-left',
      order: 0,
      isActive: true
    });
    setPromiseVideoFile(null);
  };

  // Handle promise form input change
  const handlePromiseFormChange = (field, value) => {
    setPromiseForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle promise video file selection
  const handlePromiseVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setPromiseVideoFile(file);
    }
  };

  // Save promise section (create or update)
  const savePromiseSection = async () => {
    if (!promiseForm.title.trim()) {
      showMessage('Title is required', 'error');
      return;
    }

    if (!promiseForm.description.trim()) {
      showMessage('Description is required', 'error');
      return;
    }

    if (!promiseForm.videoUrl && !promiseVideoFile) {
      showMessage('Video URL or video file is required', 'error');
      return;
    }

    setPromiseSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', promiseForm.title);
      formData.append('description', promiseForm.description);
      formData.append('layout', promiseForm.layout);
      formData.append('order', promiseForm.order);
      formData.append('isActive', promiseForm.isActive);
      
      if (promiseForm.videoUrl && promiseForm.videoUrl.startsWith('http')) {
        formData.append('videoUrl', promiseForm.videoUrl);
      }
      
      if (promiseVideoFile) {
        formData.append('video', promiseVideoFile);
      }

      const url = editingPromise 
        ? `${API_URL}/promise-sections/${editingPromise._id}`
        : `${API_URL}/promise-sections`;
      
      const method = editingPromise ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage(`Promise section ${editingPromise ? 'updated' : 'created'} successfully!`, 'success');
        closePromiseModal();
        loadPromiseSections();
      } else {
        showMessage(result.message || `Failed to ${editingPromise ? 'update' : 'create'} promise section`, 'error');
      }
    } catch (error) {
      console.error('Error saving promise section:', error);
      showMessage('Error saving promise section', 'error');
    } finally {
      setPromiseSaving(false);
    }
  };

  // Delete promise section
  const deletePromiseSection = async (sectionId) => {
    if (!window.confirm('Are you sure you want to delete this promise section? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/promise-sections/${sectionId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Promise section deleted successfully!', 'success');
        loadPromiseSections();
      } else {
        showMessage(result.message || 'Failed to delete promise section', 'error');
      }
    } catch (error) {
      console.error('Error deleting promise section:', error);
      showMessage('Error deleting promise section', 'error');
    }
  };

  // Toggle promise section active status
  const togglePromiseStatus = async (section) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !section.isActive);

      const response = await fetch(`${API_URL}/promise-sections/${section._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Promise section status updated!', 'success');
        loadPromiseSections();
      } else {
        showMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('Error updating status', 'error');
    }
  };

  // Load all hero videos
  const loadHeroVideos = async () => {
    setHeroVideosLoading(true);
    try {
      const response = await fetch(`${API_URL}/hero-videos/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setHeroVideos(result.data);
      } else {
        showMessage('Failed to load hero videos', 'error');
      }
    } catch (error) {
      console.error('Error loading hero videos:', error);
      showMessage('Error loading hero videos', 'error');
    } finally {
      setHeroVideosLoading(false);
    }
  };

  // Open modal for creating new hero video
  const openCreateHeroVideoModal = () => {
    setEditingHeroVideo(null);
    setHeroVideoForm({
      title: '',
      videoUrl: '',
      order: heroVideos.length + 1,
      isActive: true
    });
    setHeroVideoFile(null);
    setShowHeroVideoModal(true);
  };

  // Open modal for editing hero video
  const openEditHeroVideoModal = (video) => {
    setEditingHeroVideo(video);
    setHeroVideoForm({
      title: video.title,
      videoUrl: video.videoUrl,
      order: video.order,
      isActive: video.isActive
    });
    setHeroVideoFile(null);
    setShowHeroVideoModal(true);
  };

  // Close hero video modal
  const closeHeroVideoModal = () => {
    setShowHeroVideoModal(false);
    setEditingHeroVideo(null);
    setHeroVideoForm({
      title: '',
      videoUrl: '',
      order: 0,
      isActive: true
    });
    setHeroVideoFile(null);
  };

  // Handle hero video form input change
  const handleHeroVideoFormChange = (field, value) => {
    setHeroVideoForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle hero video file selection
  const handleHeroVideoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setHeroVideoFile(file);
    }
  };

  // Save hero video (create or update)
  const saveHeroVideo = async () => {
    if (!heroVideoForm.title.trim()) {
      showMessage('Title is required', 'error');
      return;
    }

    if (!heroVideoForm.videoUrl && !heroVideoFile) {
      showMessage('Video URL or video file is required', 'error');
      return;
    }

    setHeroVideoSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', heroVideoForm.title);
      formData.append('order', heroVideoForm.order);
      formData.append('isActive', heroVideoForm.isActive);
      
      if (heroVideoForm.videoUrl && heroVideoForm.videoUrl.startsWith('http')) {
        formData.append('videoUrl', heroVideoForm.videoUrl);
      }
      
      if (heroVideoFile) {
        formData.append('video', heroVideoFile);
      }

      const url = editingHeroVideo 
        ? `${API_URL}/hero-videos/${editingHeroVideo._id}`
        : `${API_URL}/hero-videos`;
      
      const method = editingHeroVideo ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage(`Hero video ${editingHeroVideo ? 'updated' : 'created'} successfully!`, 'success');
        closeHeroVideoModal();
        loadHeroVideos();
      } else {
        showMessage(result.message || `Failed to ${editingHeroVideo ? 'update' : 'create'} hero video`, 'error');
      }
    } catch (error) {
      console.error('Error saving hero video:', error);
      showMessage('Error saving hero video', 'error');
    } finally {
      setHeroVideoSaving(false);
    }
  };

  // Delete hero video
  const deleteHeroVideo = async (videoId) => {
    if (!window.confirm('Are you sure you want to delete this hero video? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/hero-videos/${videoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Hero video deleted successfully!', 'success');
        loadHeroVideos();
      } else {
        showMessage(result.message || 'Failed to delete hero video', 'error');
      }
    } catch (error) {
      console.error('Error deleting hero video:', error);
      showMessage('Error deleting hero video', 'error');
    }
  };

  // Toggle hero video active status
  const toggleHeroVideoStatus = async (video) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !video.isActive);

      const response = await fetch(`${API_URL}/hero-videos/${video._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Hero video status updated!', 'success');
        loadHeroVideos();
      } else {
        showMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('Error updating status', 'error');
    }
  };

  // Load all deco slides
  const loadDecoSlides = async () => {
    setDecoSlidesLoading(true);
    try {
      const response = await fetch(`${API_URL}/deco-slides/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setDecoSlides(result.data);
      } else {
        showMessage('Failed to load deco slides', 'error');
      }
    } catch (error) {
      console.error('Error loading deco slides:', error);
      showMessage('Error loading deco slides', 'error');
    } finally {
      setDecoSlidesLoading(false);
    }
  };

  // Open modal for creating new deco slide
  const openCreateDecoSlideModal = () => {
    setEditingDecoSlide(null);
    setDecoSlideForm({
      title: '',
      description: '',
      imageUrl: '',
      order: decoSlides.length + 1,
      isActive: true
    });
    setDecoSlideImageFile(null);
    setDecoSlideImagePreview(null);
    setShowDecoSlideModal(true);
  };

  // Open modal for editing deco slide
  const openEditDecoSlideModal = (slide) => {
    setEditingDecoSlide(slide);
    setDecoSlideForm({
      title: slide.title,
      description: slide.description,
      imageUrl: slide.imageUrl,
      order: slide.order,
      isActive: slide.isActive
    });
    setDecoSlideImageFile(null);
    setDecoSlideImagePreview(slide.imageUrl);
    setShowDecoSlideModal(true);
  };

  // Close deco slide modal
  const closeDecoSlideModal = () => {
    setShowDecoSlideModal(false);
    setEditingDecoSlide(null);
    setDecoSlideForm({
      title: '',
      description: '',
      imageUrl: '',
      order: 0,
      isActive: true
    });
    setDecoSlideImageFile(null);
    setDecoSlideImagePreview(null);
  };

  // Handle deco slide form input change
  const handleDecoSlideFormChange = (field, value) => {
    console.log(`🎨 Updating deco slide field: ${field}, value:`, value);
    setDecoSlideForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update preview if imageUrl changes
    if (field === 'imageUrl') {
      console.log('🖼️ Updating deco slide image preview:', value);
      setDecoSlideImagePreview(value);
    }
  };

  // Handle deco slide image file selection
  const handleDecoSlideImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate image file
        validateImageFile(file);
        
        console.log('📁 Deco slide image file uploaded:', file.name);
        setDecoSlideImageFile(file);
        
        // Create preview URL
        const reader = new FileReader();
        reader.onloadend = () => {
          console.log('🖼️ Deco slide image preview created');
          setDecoSlideImagePreview(reader.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        showMessage(error.message, 'error');
        e.target.value = ''; // Reset file input
      }
    }
  };

  // Save deco slide (create or update)
  const saveDecoSlide = async () => {
    // Sanitize inputs
    const sanitizedTitle = sanitizeInput(decoSlideForm.title, 200);
    const sanitizedDescription = sanitizeInput(decoSlideForm.description, 500);
    const sanitizedImageUrl = sanitizeInput(decoSlideForm.imageUrl, 1000);
    
    if (!sanitizedTitle.trim()) {
      showMessage('Title is required', 'error');
      return;
    }

    if (!sanitizedDescription.trim()) {
      showMessage('Description is required', 'error');
      return;
    }

    if (!sanitizedImageUrl && !decoSlideImageFile) {
      showMessage('Image URL or image file is required', 'error');
      return;
    }

    setDecoSlideSaving(true);
    try {
      const formData = new FormData();
      formData.append('title', sanitizedTitle);
      formData.append('description', sanitizedDescription);
      formData.append('order', parseInt(decoSlideForm.order) || 0);
      formData.append('isActive', decoSlideForm.isActive);
      
      if (sanitizedImageUrl && sanitizedImageUrl.startsWith('http')) {
        formData.append('imageUrl', sanitizedImageUrl);
      }
      
      if (decoSlideImageFile) {
        formData.append('image', decoSlideImageFile);
      }

      const url = editingDecoSlide 
        ? `${API_URL}/deco-slides/${editingDecoSlide._id}`
        : `${API_URL}/deco-slides`;
      
      const method = editingDecoSlide ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage(`Deco slide ${editingDecoSlide ? 'updated' : 'created'} successfully!`, 'success');
        closeDecoSlideModal();
        loadDecoSlides();
      } else {
        showMessage(result.message || `Failed to ${editingDecoSlide ? 'update' : 'create'} deco slide`, 'error');
      }
    } catch (error) {
      console.error('Error saving deco slide:', error);
      showMessage('Error saving deco slide', 'error');
    } finally {
      setDecoSlideSaving(false);
    }
  };

  // Delete deco slide
  const deleteDecoSlide = async (slideId) => {
    if (!window.confirm('Are you sure you want to delete this deco slide? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/deco-slides/${slideId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Deco slide deleted successfully!', 'success');
        loadDecoSlides();
      } else {
        showMessage(result.message || 'Failed to delete deco slide', 'error');
      }
    } catch (error) {
      console.error('Error deleting deco slide:', error);
      showMessage('Error deleting deco slide', 'error');
    }
  };

  // Toggle deco slide active status
  const toggleDecoSlideStatus = async (slide) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !slide.isActive);

      const response = await fetch(`${API_URL}/deco-slides/${slide._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Deco slide status updated!', 'success');
        loadDecoSlides();
      } else {
        showMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('Error updating status', 'error');
    }
  };

  // Load all catering cards
  const loadCateringCards = async () => {
    setCateringCardsLoading(true);
    try {
      const response = await fetch(`${API_URL}/catering-cards/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setCateringCards(result.data);
      } else {
        showMessage('Failed to load catering cards', 'error');
      }
    } catch (error) {
      console.error('Error loading catering cards:', error);
      showMessage('Error loading catering cards', 'error');
    } finally {
      setCateringCardsLoading(false);
    }
  };

  // Open modal for creating new catering card
  const openCreateCateringCardModal = () => {
    setEditingCateringCard(null);
    setCateringCardForm({
      name: '',
      description: '',
      videoUrl: '',
      title: '',
      subtitle: '',
      order: cateringCards.length + 1,
      isActive: true
    });
    setCateringCardVideoFile(null);
    setCateringCardVideoPreview(null);
    setShowCateringCardModal(true);
  };

  // Open modal for editing catering card
  const openEditCateringCardModal = (card) => {
    setEditingCateringCard(card);
    setCateringCardForm({
      name: card.name,
      description: card.description,
      videoUrl: card.videoUrl,
      title: card.title,
      subtitle: card.subtitle,
      order: card.order,
      isActive: card.isActive
    });
    setCateringCardVideoFile(null);
    setCateringCardVideoPreview(card.videoUrl);
    setShowCateringCardModal(true);
  };

  // Close catering card modal
  const closeCateringCardModal = () => {
    setShowCateringCardModal(false);
    setEditingCateringCard(null);
    setCateringCardForm({
      name: '',
      description: '',
      videoUrl: '',
      title: '',
      subtitle: '',
      order: 0,
      isActive: true
    });
    setCateringCardVideoFile(null);
    setCateringCardVideoPreview(null);
  };

  // Handle catering card form input change
  const handleCateringCardFormChange = (field, value) => {
    console.log(`🍽️ Updating catering card field: ${field}, value:`, value);
    setCateringCardForm(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Update preview if videoUrl changes
    if (field === 'videoUrl') {
      console.log('🎥 Updating catering card video preview:', value);
      setCateringCardVideoPreview(value);
    }
  };

  // Handle catering card video file selection
  const handleCateringCardVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        // Validate video file
        validateVideoFile(file);
        
        console.log('📁 Catering card video file uploaded:', file.name);
        setCateringCardVideoFile(file);
        
        // Create preview URL
        const url = URL.createObjectURL(file);
        console.log('🎥 Catering card video preview created');
        setCateringCardVideoPreview(url);
      } catch (error) {
        showMessage(error.message, 'error');
        e.target.value = ''; // Clear the input
      }
    }
  };

  // Save catering card (create or update)
  const saveCateringCard = async () => {
    // Sanitize inputs
    const name = sanitizeInput(cateringCardForm.name, 200);
    const description = sanitizeInput(cateringCardForm.description, 500);
    const title = sanitizeInput(cateringCardForm.title, 200);
    const subtitle = sanitizeInput(cateringCardForm.subtitle, 200);
    const videoUrl = sanitizeInput(cateringCardForm.videoUrl, 1000);

    if (!name.trim()) {
      showMessage('Card name is required', 'error');
      return;
    }

    if (!description.trim()) {
      showMessage('Card description is required', 'error');
      return;
    }

    if (!title.trim()) {
      showMessage('Title is required', 'error');
      return;
    }

    if (!subtitle.trim()) {
      showMessage('Subtitle is required', 'error');
      return;
    }

    if (!videoUrl && !cateringCardVideoFile) {
      showMessage('Video URL or video file is required', 'error');
      return;
    }

    setCateringCardSaving(true);
    try {
      const formData = new FormData();
      formData.append('name', name);
      formData.append('description', description);
      formData.append('title', title);
      formData.append('subtitle', subtitle);
      formData.append('order', cateringCardForm.order);
      formData.append('isActive', cateringCardForm.isActive);
      
      if (videoUrl && videoUrl.startsWith('http')) {
        formData.append('videoUrl', videoUrl);
      }
      
      if (cateringCardVideoFile) {
        formData.append('video', cateringCardVideoFile);
      }

      const url = editingCateringCard 
        ? `${API_URL}/catering-cards/${editingCateringCard._id}`
        : `${API_URL}/catering-cards`;
      
      const method = editingCateringCard ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage(`Catering card ${editingCateringCard ? 'updated' : 'created'} successfully!`, 'success');
        closeCateringCardModal();
        loadCateringCards();
      } else {
        showMessage(result.message || `Failed to ${editingCateringCard ? 'update' : 'create'} catering card`, 'error');
      }
    } catch (error) {
      console.error('Error saving catering card:', error);
      showMessage('Error saving catering card', 'error');
    } finally {
      setCateringCardSaving(false);
    }
  };

  // Delete catering card
  const deleteCateringCard = async (cardId) => {
    if (!window.confirm('Are you sure you want to delete this catering card? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/catering-cards/${cardId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Catering card deleted successfully!', 'success');
        loadCateringCards();
      } else {
        showMessage(result.message || 'Failed to delete catering card', 'error');
      }
    } catch (error) {
      console.error('Error deleting catering card:', error);
      showMessage('Error deleting catering card', 'error');
    }
  };

  // Toggle catering card active status
  const toggleCateringCardStatus = async (card) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !card.isActive);

      const response = await fetch(`${API_URL}/catering-cards/${card._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Catering card status updated!', 'success');
        loadCateringCards();
      } else {
        showMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('Error updating status', 'error');
    }
  };

  // Load all event types
  const loadEventTypes = async () => {
    setEventTypesLoading(true);
    try {
      const response = await fetch(`${API_URL}/events-frontend/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        setEventTypes(result.data);
      } else {
        showMessage('Failed to load event types', 'error');
      }
    } catch (error) {
      console.error('Error loading event types:', error);
      showMessage('Error loading event types', 'error');
    } finally {
      setEventTypesLoading(false);
    }
  };

  // Preview mode handlers
  const handleEditSection = (section) => {
    console.log('handleEditSection called with:', section);
    console.log('Current editingSection:', editingSection);
    setEditingSection(section);
    console.log('Set editingSection to:', section);
  };

  const handleDeleteSection = (section) => {
    if (window.confirm(`Are you sure you want to remove the ${section} section?`)) {
      handleEventFormChange(`${section}.enabled`, false);
      showMessage(`${section} section removed`, 'success');
    }
  };

  const handleAddSection = (section) => {
    console.log(`🔧 handleAddSection called for: ${section}`);
    console.log(`📊 Current section state:`, eventForm[section]);
    
    if (section === 'dynamicSections') {
      // Don't toggle enabled for dynamic sections, just open the editor
      setEditingSection(section);
      setShowAddSectionMenu(false);
      showMessage('Dynamic sections editor opened', 'success');
    } else {
      // Ensure the section structure is properly initialized
      if (section === 'helpYouPlan') {
        // Initialize helpYouPlan with full structure if not exists or incomplete
        if (!eventForm.helpYouPlan || !eventForm.helpYouPlan.sectionHeading) {
          setEventForm(prev => ({
            ...prev,
            helpYouPlan: {
              enabled: false,
              sectionHeading: 'What We Can Help You Plan',
              cards: prev.helpYouPlan?.cards || []
            }
          }));
        }
      } else if (section === 'highlights') {
        // Initialize highlights with full structure if not exists or incomplete
        if (!eventForm.highlights || !eventForm.highlights.title) {
          setEventForm(prev => ({
            ...prev,
            highlights: {
              enabled: false,
              title: 'Event Highlights',
              subtitle: 'Creating Moments That Last Forever',
              cards: prev.highlights?.cards || []
            }
          }));
        }
      } else if (section === 'fazyraTouch') {
        // Initialize fazyraTouch with full structure if not exists or incomplete
        if (!eventForm.fazyraTouch || !eventForm.fazyraTouch.title) {
          setEventForm(prev => ({
            ...prev,
            fazyraTouch: {
              enabled: false,
              image1: '',
              image1Alt: '',
              image1Preview: null,
              image2: '',
              image2Alt: '',
              image2Preview: null,
              image3: '',
              image3Alt: '',
              image3Preview: null,
              title: 'The Fazyra Touch',
              subtitle: 'Where Every Detail Tells Your Story',
              mainDescription: '',
              features: [
                { label: '', description: '' },
                { label: '', description: '' },
                { label: '', description: '' }
              ],
              closingDescription: '',
              ...(prev.fazyraTouch || {})
            }
          }));
        }
      }
      
      const isEnabled = eventForm[section]?.enabled;
      if (isEnabled) {
        showMessage(`Opening ${section} section editor`, 'info');
      } else {
        showMessage(`Add content to enable ${section} section`, 'info');
      }
      
      console.log(`🎯 Setting editingSection to: ${section}`);
      setEditingSection(section);
      setShowAddSectionMenu(false);
    }
  };

  const handleSaveDraft = async () => {
    showMessage('Draft saved successfully', 'success');
  };

  const closeSectionEditor = () => {
    // Auto-enable or disable sections based on content when closing
    if (editingSection === 'helpYouPlan') {
      const hasCards = eventForm.helpYouPlan?.cards && eventForm.helpYouPlan.cards.length > 0;
      if (hasCards) {
        // Enable the section if it has cards
        handleEventFormChange('helpYouPlan.enabled', true);
        console.log('Auto-enabled helpYouPlan section (cards added)');
      } else {
        // Disable if no cards
        handleEventFormChange('helpYouPlan.enabled', false);
        console.log('Auto-disabled helpYouPlan section (no cards added)');
      }
    } else if (editingSection === 'highlights') {
      const hasCards = eventForm.highlights?.cards && eventForm.highlights.cards.length > 0;
      if (hasCards) {
        handleEventFormChange('highlights.enabled', true);
        console.log('Auto-enabled highlights section (cards added)');
      } else {
        handleEventFormChange('highlights.enabled', false);
        console.log('Auto-disabled highlights section (no cards added)');
      }
    } else if (editingSection === 'fazyraTouch') {
      const hasContent = eventForm.fazyraTouch?.mainDescription || 
                        eventForm.fazyraTouch?.closingDescription ||
                        (eventForm.fazyraTouch?.features && eventForm.fazyraTouch.features.some(f => f.label || f.description));
      if (hasContent) {
        handleEventFormChange('fazyraTouch.enabled', true);
        console.log('Auto-enabled fazyraTouch section (content added)');
      } else {
        handleEventFormChange('fazyraTouch.enabled', false);
        console.log('Auto-disabled fazyraTouch section (no content added)');
      }
    }
    
    setEditingSection(null);
    setShowAddSectionMenu(false); // Close the dropdown menu as well
  };

  // Open modal for creating new event
  const openCreateEventModal = () => {
    setPreviewMode(true);
    setEditingSection('basic'); // Start with basic info editor
    setEditingEvent(null);
    setEventForm({
      name: '',
      description: '',
      posterImage: '',
      order: eventTypes.length + 1,
      isActive: true,
      fazyraTouch: {
        enabled: false,
        image1: '',
        image1Alt: '',
        image1Preview: null,
        image2: '',
        image2Alt: '',
        image2Preview: null,
        image3: '',
        image3Alt: '',
        image3Preview: null,
        title: 'The Fazyra Touch',
        subtitle: 'Where Every Detail Tells Your Story',
        mainDescription: '',
        features: [
          { label: '', description: '' },
          { label: '', description: '' },
          { label: '', description: '' }
        ],
        closingDescription: ''
      },
      highlights: {
        enabled: false,
        title: 'Event Highlights',
        subtitle: 'Creating Moments That Last Forever',
        cards: []
      },
      helpYouPlan: {
        enabled: false,
        sectionHeading: 'What We Can Help You Plan',
        cards: []
      }
    });
    setEventImageFile(null);
    setEventImagePreview(null);
    setFazyraTouchImageFiles({
      image1: null,
      image2: null,
      image3: null
    });
    setShowEventModal(true);
  };

  // Open modal for editing event
  const openEditEventModal = (event) => {
    setPreviewMode(true);
    setEditingSection(null); // Start in preview mode, not editing any section
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      description: event.description || '',
      posterImage: event.posterImage,
      order: event.order,
      isActive: event.isActive,
      fazyraTouch: event.fazyraTouch ? {
        ...event.fazyraTouch,
        image1Preview: event.fazyraTouch.image1 || null,
        image2Preview: event.fazyraTouch.image2 || null,
        image3Preview: event.fazyraTouch.image3 || null,
        features: event.fazyraTouch.features && event.fazyraTouch.features.length > 0 
          ? event.fazyraTouch.features 
          : [
            { label: '', description: '' },
            { label: '', description: '' },
            { label: '', description: '' }
          ]
      } : {
        enabled: false,
        image1: '',
        image1Alt: '',
        image1Preview: null,
        image2: '',
        image2Alt: '',
        image2Preview: null,
        image3: '',
        image3Alt: '',
        image3Preview: null,
        title: 'The Fazyra Touch',
        subtitle: 'Where Every Detail Tells Your Story',
        mainDescription: '',
        features: [
          { label: '', description: '' },
          { label: '', description: '' },
          { label: '', description: '' }
        ],
        closingDescription: ''
      },
      highlights: event.highlights ? {
        ...event.highlights,
        cards: event.highlights.cards || []
      } : {
        enabled: false,
        title: 'Event Highlights',
        subtitle: 'Creating Moments That Last Forever',
        cards: []
      },
      helpYouPlan: event.helpYouPlan ? {
        ...event.helpYouPlan,
        cards: event.helpYouPlan.cards || []
      } : {
        enabled: false,
        sectionHeading: 'What We Can Help You Plan',
        cards: []
      },
      dynamicSections: event.dynamicSections && event.dynamicSections.length > 0 ? 
        event.dynamicSections.map(section => ({
          ...section,
          imagePreview: section.imageUrl || null
        })) : []
    });
    setEventImageFile(null);
    setFazyraTouchImageFiles({
      image1: null,
      image2: null,
      image3: null
    });
    setHelpYouPlanVideoFiles({});
    setHelpYouPlanVideoPreviews({});
    setDynamicSectionImageFiles({});
    // Set preview to existing image
    setEventImagePreview(event.posterImage);
    setShowEventModal(true);
  };

  // Close event modal
  const closeEventModal = () => {
    setShowEventModal(false);
    setEditingEvent(null);
    setPreviewMode(false);
    setEditingSection(null);
    setEventForm({
      name: '',
      description: '',
      posterImage: '',
      order: 0,
      isActive: true,
      fazyraTouch: {
        enabled: false,
        image1: '',
        image1Alt: '',
        image1Preview: null,
        image2: '',
        image2Alt: '',
        image2Preview: null,
        image3: '',
        image3Alt: '',
        image3Preview: null,
        title: 'The Fazyra Touch',
        subtitle: 'Where Every Detail Tells Your Story',
        mainDescription: '',
        features: [
          { label: '', description: '' },
          { label: '', description: '' },
          { label: '', description: '' }
        ],
        closingDescription: ''
      },
      highlights: {
        enabled: false,
        title: 'Event Highlights',
        subtitle: 'Creating Moments That Last Forever',
        cards: []
      },
      helpYouPlan: {
        enabled: false,
        sectionHeading: 'What We Can Help You Plan',
        cards: []
      }
    });
    setEventImageFile(null);
    setEventImagePreview(null);
    setFazyraTouchImageFiles({
      image1: null,
      image2: null,
      image3: null
    });
    setHelpYouPlanVideoFiles({});
    setHelpYouPlanVideoPreviews({});
  };

  // Handle event form input change
  const handleEventFormChange = (field, value) => {
    // Handle nested fazyraTouch fields
    if (field.startsWith('fazyraTouch.')) {
      const nestedField = field.split('.')[1];
      console.log('🎨 Updating Fazyra Touch field:', nestedField, 'with value:', value);
      setEventForm(prev => ({
        ...prev,
        fazyraTouch: {
          ...prev.fazyraTouch,
          [nestedField]: value
        }
      }));
      
      // If fazyraTouch image URL is changed, update preview
      if (nestedField.startsWith('image') && !nestedField.includes('Alt')) {
        console.log('🖼️ Updating Fazyra Touch image preview for:', nestedField);
        setEventForm(prev => ({
          ...prev,
          fazyraTouch: {
            ...prev.fazyraTouch,
            [`${nestedField}Preview`]: value
          }
        }));
      }
    } 
    // Handle nested highlights fields
    else if (field.startsWith('highlights.')) {
      const nestedField = field.split('.')[1];
      console.log('Updating highlights field:', nestedField, 'with value:', value);
      setEventForm(prev => ({
        ...prev,
        highlights: {
          ...prev.highlights,
          [nestedField]: value
        }
      }));
    } 
    // Handle nested helpYouPlan fields
    else if (field.startsWith('helpYouPlan.')) {
      const nestedField = field.split('.')[1];
      console.log('Updating helpYouPlan field:', nestedField, 'with value:', value);
      setEventForm(prev => ({
        ...prev,
        helpYouPlan: {
          ...prev.helpYouPlan,
          [nestedField]: value
        }
      }));
    }
    else {
      setEventForm(prev => ({
        ...prev,
        [field]: value
      }));
      
      // If poster image URL is changed, update preview
      if (field === 'posterImage') {
        setEventImagePreview(value);
      }
    }
  };

  // Handle fazyraTouch image file selection
  const handleFazyraTouchImageChange = (imageKey, file) => {
    if (file) {
      console.log('📁 Fazyra Touch image file uploaded:', imageKey, file.name);
      setFazyraTouchImageFiles(prev => ({
        ...prev,
        [imageKey]: file
      }));
      
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        console.log('🖼️ Fazyra Touch image preview created for:', imageKey);
        setEventForm(prev => ({
          ...prev,
          fazyraTouch: {
            ...prev.fazyraTouch,
            [`${imageKey}Preview`]: reader.result
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle helpYouPlan video file selection
  const handleHelpYouPlanVideoChange = (cardIndex, file) => {
    if (file) {
      console.log('📁 Help You Plan video file uploaded for card', cardIndex, ':', file.name);
      setHelpYouPlanVideoFiles(prev => ({
        ...prev,
        [cardIndex]: file
      }));
      
      // Create video preview URL
      const url = URL.createObjectURL(file);
      console.log('🎥 Help You Plan video preview created for card', cardIndex);
      setHelpYouPlanVideoPreviews(prev => ({
        ...prev,
        [cardIndex]: url
      }));
      
      // Update the card to show file name
      const updatedCards = [...(eventForm.helpYouPlan.cards || [])];
      updatedCards[cardIndex] = {
        ...updatedCards[cardIndex],
        videoFileName: file.name
      };
      handleEventFormChange('helpYouPlan.cards', updatedCards);
    }
  };

  // Handle dynamic section image change
  const handleDynamicSectionImageChange = (fieldName, file) => {
    if (file) {
      console.log('Dynamic section image uploaded:', fieldName, file.name);
      setDynamicSectionImageFiles(prev => ({
        ...prev,
        [fieldName]: file
      }));
    } else {
      // Remove file if null (image was removed)
      setDynamicSectionImageFiles(prev => {
        const updated = { ...prev };
        delete updated[fieldName];
        return updated;
      });
    }
  };

  // Handle event image selection
  const handleEventImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEventImageFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setEventImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save event (create or update)
  const saveEvent = async () => {
    console.log('🚀 saveEvent called');
    
    if (!eventForm.name.trim()) {
      showMessage('Event name is required', 'error');
      return;
    }

    // Check for large video files
    const videoFiles = Object.values(helpYouPlanVideoFiles);
    const totalVideoSize = videoFiles.reduce((acc, file) => acc + (file ? file.size : 0), 0);
    const maxSize = 500 * 1024 * 1024; // 500 MB limit
    
    if (totalVideoSize > maxSize) {
      const sizeMB = (totalVideoSize / (1024 * 1024)).toFixed(2);
      showMessage(`Video files are too large (${sizeMB}MB). Please use videos under 500MB or compress them.`, 'error');
      console.error('❌ Video files too large:', { totalVideoSize, maxSize, videoFiles: videoFiles.map(f => ({ name: f.name, size: (f.size / (1024 * 1024)).toFixed(2) + 'MB' })) });
      return;
    }
    
    console.log('📦 Video file sizes check passed:', { 
      totalSizeMB: (totalVideoSize / (1024 * 1024)).toFixed(2),
      videoCount: videoFiles.length 
    });

    setEventSaving(true);
    try {
      console.log('📦 Creating FormData...');
      const formData = new FormData();
      formData.append('name', eventForm.name);
      formData.append('description', eventForm.description);
      formData.append('order', eventForm.order);
      formData.append('isActive', eventForm.isActive);
      
      // Generate slug from name for new events
      if (!editingEvent && eventForm.name) {
        const slug = eventForm.name
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');
        formData.append('slug', slug);
        console.log('📝 Generated slug:', slug);
      }
      
      // Add createdBy field for new events
      if (!editingEvent) {
        // Try multiple ways to get the admin ID
        let adminId = null;
        
        // Method 1: From user object (try multiple field names)
        if (user) {
          adminId = user._id || user.id || user.adminId || user.userId;
          console.log('📍 Method 1 - From user object:', { user, adminId });
        }
        
        // Method 2: From localStorage user
        if (!adminId) {
          try {
            const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
            adminId = storedUser._id || storedUser.id || storedUser.adminId;
            console.log('� Method 2 - From localStorage:', { storedUser, adminId });
          } catch (e) {
            console.log('📍 Method 2 - localStorage parse error:', e);
          }
        }
        
        // Method 3: Decode JWT token to get user ID
        if (!adminId && token) {
          try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
              return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
            }).join(''));
            const decoded = JSON.parse(jsonPayload);
            adminId = decoded.id || decoded._id || decoded.adminId || decoded.userId;
            console.log('📍 Method 3 - From JWT token:', { decoded, adminId });
          } catch (e) {
            console.log('📍 Method 3 - JWT decode error:', e);
          }
        }
        
        console.log('👤 Final Admin ID check:', {
          finalAdminId: adminId,
          hasUser: !!user,
          hasToken: !!token
        });
        
        if (adminId) {
          formData.append('createdBy', adminId);
          console.log('✅ Added createdBy:', adminId);
        } else {
          console.error('❌ No admin ID found! User object:', user);
          console.error('❌ Token:', token ? 'exists' : 'missing');
          setMessage({ text: 'Admin ID not found. Please log out and log in again.', type: 'error' });
          throw new Error('Admin ID not found. Please log in again.');
        }
      }
      
      // If it's an external URL (http/https), send as posterImage string
      if (eventForm.posterImage && eventForm.posterImage.startsWith('http')) {
        formData.append('posterImage', eventForm.posterImage);
      }
      
      // If a new file is selected, append it
      if (eventImageFile) {
        formData.append('posterImage', eventImageFile);
      }

      // Add Fazyra Touch data
      const fazyraTouchData = {
        enabled: eventForm.fazyraTouch.enabled,
        title: eventForm.fazyraTouch.title,
        subtitle: eventForm.fazyraTouch.subtitle,
        mainDescription: eventForm.fazyraTouch.mainDescription,
        features: eventForm.fazyraTouch.features.filter(f => f.label && f.description),
        closingDescription: eventForm.fazyraTouch.closingDescription,
        image1Alt: eventForm.fazyraTouch.image1Alt,
        image2Alt: eventForm.fazyraTouch.image2Alt,
        image3Alt: eventForm.fazyraTouch.image3Alt
      };

      // Handle Fazyra Touch images
      if (eventForm.fazyraTouch.image1 && eventForm.fazyraTouch.image1.startsWith('http')) {
        fazyraTouchData.image1 = eventForm.fazyraTouch.image1;
      }
      if (eventForm.fazyraTouch.image2 && eventForm.fazyraTouch.image2.startsWith('http')) {
        fazyraTouchData.image2 = eventForm.fazyraTouch.image2;
      }
      if (eventForm.fazyraTouch.image3 && eventForm.fazyraTouch.image3.startsWith('http')) {
        fazyraTouchData.image3 = eventForm.fazyraTouch.image3;
      }

      formData.append('fazyraTouch', JSON.stringify(fazyraTouchData));

      // Append Fazyra Touch image files
      if (fazyraTouchImageFiles.image1) {
        formData.append('fazyraTouchImage1', fazyraTouchImageFiles.image1);
      }
      if (fazyraTouchImageFiles.image2) {
        formData.append('fazyraTouchImage2', fazyraTouchImageFiles.image2);
      }
      if (fazyraTouchImageFiles.image3) {
        formData.append('fazyraTouchImage3', fazyraTouchImageFiles.image3);
      }

      // Add Highlights data
      const highlightsData = {
        enabled: eventForm.highlights.enabled,
        title: eventForm.highlights.title,
        subtitle: eventForm.highlights.subtitle,
        cards: (eventForm.highlights.cards || []).filter(card => 
          card.heading && card.heading.trim() !== '' && 
          card.description && card.description.trim() !== ''
        )
      };
      formData.append('highlights', JSON.stringify(highlightsData));

      // Add Help You Plan data
      // DON'T filter cards - preserve original indices to match video file uploads
      const helpYouPlanData = {
        enabled: eventForm.helpYouPlan.enabled,
        sectionHeading: eventForm.helpYouPlan.sectionHeading,
        cards: (eventForm.helpYouPlan.cards || []).map((card, originalIndex) => {
          // Check if this card has all required fields
          const hasName = card.cardName && card.cardName.trim() !== '';
          const hasDescription = card.cardDescription && card.cardDescription.trim() !== '';
          const hasVideo = (card.videoUrl && card.videoUrl.trim() !== '') || helpYouPlanVideoFiles[originalIndex];
          const hasVideoTitle = card.videoTitle && card.videoTitle.trim() !== '';
          const hasVideoSubtitle = card.videoSubtitle && card.videoSubtitle.trim() !== '';
          
          console.log(`📋 Card ${originalIndex} validation:`, {
            hasName,
            hasDescription,
            hasVideo,
            hasVideoTitle,
            hasVideoSubtitle,
            hasVideoFile: !!helpYouPlanVideoFiles[originalIndex],
            videoUrl: card.videoUrl,
            cardIndex: originalIndex
          });
          
          // Remove videoFileName from data sent to backend (it's just for UI)
          const { videoFileName, ...cardData } = card;
          
          // Preserve ALL cards to maintain index alignment with video files
          return cardData;
        })
      };
      
      console.log('📊 Help You Plan data being saved:', {
        enabled: helpYouPlanData.enabled,
        cardCount: helpYouPlanData.cards.length,
        videoFilesCount: Object.keys(helpYouPlanVideoFiles).length
      });
      
      formData.append('helpYouPlan', JSON.stringify(helpYouPlanData));

      // Append video files for Help You Plan cards
      console.log('🎬 Video files state:', helpYouPlanVideoFiles);
      Object.keys(helpYouPlanVideoFiles).forEach(cardIndex => {
        if (helpYouPlanVideoFiles[cardIndex]) {
          const file = helpYouPlanVideoFiles[cardIndex];
          const fieldName = `helpYouPlanVideo${cardIndex}`;
          console.log(`📤 Appending video file for card ${cardIndex}:`, {
            fieldName,
            fileName: file.name,
            fileSize: (file.size / (1024 * 1024)).toFixed(2) + 'MB',
            fileType: file.type
          });
          formData.append(fieldName, file);
        }
      });

      // Add Dynamic Sections data
      const dynamicSectionsData = (eventForm.dynamicSections || []).map((section, index) => {
        // Remove imagePreview from data sent to backend (it's just for UI)
        const { imagePreview, ...sectionData } = section;
        return sectionData;
      });
      formData.append('dynamicSections', JSON.stringify(dynamicSectionsData));

      // Append image files for Dynamic Sections
      Object.keys(dynamicSectionImageFiles).forEach(fieldName => {
        if (dynamicSectionImageFiles[fieldName]) {
          formData.append(fieldName, dynamicSectionImageFiles[fieldName]);
        }
      });

      const url = editingEvent 
        ? `${API_URL}/events-frontend/${editingEvent._id}`
        : `${API_URL}/events-frontend`;
      
      const method = editingEvent ? 'PUT' : 'POST';

      console.log('📡 Sending request:', { url, method, hasToken: !!token });
      
      // Create a timeout promise - increased to 15 minutes for large 4K videos
      const timeoutDuration = 900000; // 15 minutes for large video uploads (was 5 min)
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Upload timeout after 15 minutes - file may be too large or connection too slow')), timeoutDuration);
      });
      
      // Show upload progress message
      if (totalVideoSize > 10 * 1024 * 1024) { // > 10MB
        const sizeMB = (totalVideoSize / (1024 * 1024)).toFixed(2);
        showMessage(`Uploading ${sizeMB}MB of video... This may take a few minutes. Please wait.`, 'info');
        console.log(`⏳ Large upload detected (${sizeMB}MB), please wait...`);
      }
      
      const fetchPromise = fetch(url, {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      // Race between fetch and timeout
      const response = await Promise.race([fetchPromise, timeoutPromise]);

      console.log('📨 Response status:', response.status, response.statusText);
      const result = await response.json();
      console.log('📊 Response data:', result);
      
      if (result.success) {
        console.log('✅ Event saved successfully!');
        showMessage(`Event ${editingEvent ? 'updated' : 'created'} successfully!`, 'success');
        closeEventModal();
        loadEventTypes();
      } else {
        console.error('❌ Save failed:', result.message);
        showMessage(result.message || `Failed to ${editingEvent ? 'update' : 'create'} event`, 'error');
      }
    } catch (error) {
      console.error('💥 Error saving event:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack
      });
      showMessage('Error saving event', 'error');
    } finally {
      setEventSaving(false);
    }
  };

  // Delete event
  const deleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event type? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/events-frontend/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Event deleted successfully!', 'success');
        loadEventTypes();
      } else {
        showMessage(result.message || 'Failed to delete event', 'error');
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      showMessage('Error deleting event', 'error');
    }
  };

  // Toggle event active status
  const toggleEventStatus = async (event) => {
    try {
      const formData = new FormData();
      formData.append('isActive', !event.isActive);

      const response = await fetch(`${API_URL}/events-frontend/${event._id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const result = await response.json();
      
      if (result.success) {
        showMessage('Event status updated!', 'success');
        loadEventTypes();
      } else {
        showMessage(result.message || 'Failed to update status', 'error');
      }
    } catch (error) {
      console.error('Error toggling status:', error);
      showMessage('Error updating status', 'error');
    }
  };

  // Load event types on mount
  useEffect(() => {
    loadEventTypes();
  }, []);

  // Load homepage description when description tab is active
  useEffect(() => {
    if (activeTab === 'description') {
      loadHomepageDescription();
    } else if (activeTab === 'promiseSections') {
      loadPromiseSections();
    } else if (activeTab === 'heroVideos') {
      loadHeroVideos();
    } else if (activeTab === 'decoSlides') {
      loadDecoSlides();
    } else if (activeTab === 'cateringCards') {
      loadCateringCards();
    }
  }, [activeTab]);

  return (
    <PageContainer>
      <PageHeader>
        <Title>
          <FiGrid style={{ marginRight: '10px' }} />
          Homepage Management
        </Title>
        <Subtitle>Manage homepage content including event types and description</Subtitle>
      </PageHeader>

      {/* Tabs */}
      <TabsContainer>
        <Tab 
          active={activeTab === 'eventTypes'} 
          onClick={() => setActiveTab('eventTypes')}
        >
          <FiGrid style={{ marginRight: '8px' }} />
          Event Types
        </Tab>
        <Tab 
          active={activeTab === 'description'} 
          onClick={() => setActiveTab('description')}
        >
          <FiFileText style={{ marginRight: '8px' }} />
          Homepage Description
        </Tab>
        <Tab 
          active={activeTab === 'promiseSections'} 
          onClick={() => setActiveTab('promiseSections')}
        >
          <FiCheck style={{ marginRight: '8px' }} />
          Promise Sections
        </Tab>
        <Tab 
          active={activeTab === 'heroVideos'} 
          onClick={() => setActiveTab('heroVideos')}
        >
          <FiImage style={{ marginRight: '8px' }} />
          Hero Videos
        </Tab>
        <Tab 
          active={activeTab === 'decoSlides'} 
          onClick={() => setActiveTab('decoSlides')}
        >
          <FiImage style={{ marginRight: '8px' }} />
          Deco Slides
        </Tab>
        <Tab 
          active={activeTab === 'cateringCards'} 
          onClick={() => setActiveTab('cateringCards')}
        >
          <FiImage style={{ marginRight: '8px' }} />
          Catering Cards
        </Tab>
      </TabsContainer>

      {message.text && (
        <Message type={message.type}>
          {message.text}
        </Message>
      )}

      {/* Event Types Tab Content */}
      {activeTab === 'eventTypes' && (
        <>
          <QuickActions>
            <ActionButton 
              variant="primary" 
              onClick={openCreateEventModal}
            >
              <FiPlus style={{ marginRight: '5px' }} />
              Add New Event Type
            </ActionButton>
            <ActionButton 
              variant="secondary" 
              onClick={loadEventTypes}
              disabled={eventTypesLoading}
            >
              <FiRefreshCw style={{ marginRight: '5px' }} />
              {eventTypesLoading ? 'Loading...' : 'Refresh'}
            </ActionButton>
          </QuickActions>

          <ContentSection>
        {eventTypesLoading ? (
          <LoadingStatus>Loading event types...</LoadingStatus>
        ) : eventTypes.length === 0 ? (
          <NoDataStatus>
            <FiGrid size={48} color="#ccc" />
            <p>No event types found.</p>
            <p>Click "Add New Event Type" to create one.</p>
          </NoDataStatus>
        ) : (
          <EventTypesGrid>
            {eventTypes
              .sort((a, b) => a.order - b.order)
              .map((event) => (
                <EventTypeCard key={event._id}>
                  <EventTypeImage>
                    {event.posterImage ? (
                      <img 
                        src={event.posterImage.startsWith('http') 
                          ? event.posterImage 
                          : event.posterImage
                        } 
                        alt={event.name}
                        onError={(e) => e.target.src = 'https://via.placeholder.com/400x300?text=No+Image'}
                      />
                    ) : (
                      <NoImagePlaceholder>
                        <FiImage size={48} color="#ccc" />
                        <span>No Image</span>
                      </NoImagePlaceholder>
                    )}
                    <EventTypeOrder>#{event.order}</EventTypeOrder>
                    <EventTypeStatus isActive={event.isActive}>
                      {event.isActive ? '● Active' : '○ Inactive'}
                    </EventTypeStatus>
                  </EventTypeImage>
                  <EventTypeDetails>
                    <EventTypeName>{event.name}</EventTypeName>
                    <EventTypeSlug>/{event.slug}</EventTypeSlug>
                    <EventTypeDescription>
                      {event.description || 'No description'}
                    </EventTypeDescription>
                    <EventTypeActions>
                      <EventActionBtn 
                        variant="edit" 
                        onClick={() => openEditEventModal(event)}
                      >
                        <FiEdit2 size={14} /> Edit
                      </EventActionBtn>
                      <EventActionBtn 
                        variant={event.isActive ? 'inactive' : 'active'} 
                        onClick={() => toggleEventStatus(event)}
                      >
                        {event.isActive ? 'Deactivate' : 'Activate'}
                      </EventActionBtn>
                      <EventActionBtn 
                        variant="delete" 
                        onClick={() => deleteEvent(event._id)}
                      >
                        <FiTrash2 size={14} /> Delete
                      </EventActionBtn>
                    </EventTypeActions>
                  </EventTypeDetails>
                </EventTypeCard>
              ))}
          </EventTypesGrid>
        )}
      </ContentSection>
        </>
      )}

      {/* Homepage Description Tab Content */}
      {activeTab === 'description' && (
        <DescriptionContainer>
          <DescriptionCard>
            <DescriptionFormGroup>
              <DescriptionLabel>Homepage Description</DescriptionLabel>
              <DescriptionHint>
                This text appears below the FAZYRA logo on the homepage
              </DescriptionHint>
              {descriptionLoading ? (
                <LoadingStatus>Loading description...</LoadingStatus>
              ) : (
                <>
                  <DescriptionTextArea
                    value={homepageDescription}
                    onChange={(e) => setHomepageDescription(e.target.value)}
                    placeholder="Enter the homepage description text..."
                    maxLength={MAX_DESCRIPTION_CHARS}
                  />
                  <CharacterCount warning={homepageDescription.length > MAX_DESCRIPTION_CHARS * 0.9}>
                    {homepageDescription.length} / {MAX_DESCRIPTION_CHARS} characters
                  </CharacterCount>
                </>
              )}
            </DescriptionFormGroup>

            <SaveDescriptionButton 
              onClick={saveHomepageDescription} 
              disabled={descriptionSaving || !homepageDescription.trim() || descriptionLoading}
            >
              <FiSave />
              {descriptionSaving ? 'Saving...' : 'Save Changes'}
            </SaveDescriptionButton>
          </DescriptionCard>

          <DescriptionCard>
            <PreviewSection>
              <DescriptionPreviewTitle>
                <FiInfo />
                Preview
              </DescriptionPreviewTitle>
              <PreviewBox>
                <PreviewText>
                  {homepageDescription || 'Your description will appear here...'}
                </PreviewText>
              </PreviewBox>
            </PreviewSection>
          </DescriptionCard>
        </DescriptionContainer>
      )}

      {/* Promise Sections Tab Content */}
      {activeTab === 'promiseSections' && (
        <>
          <QuickActions>
            <ActionButton 
              variant="secondary" 
              onClick={loadPromiseSections}
              disabled={promiseSectionsLoading}
            >
              <FiRefreshCw style={{ marginRight: '5px' }} />
              {promiseSectionsLoading ? 'Loading...' : 'Refresh'}
            </ActionButton>
          </QuickActions>

          <ContentSection>
            {promiseSectionsLoading ? (
              <LoadingStatus>Loading promise sections...</LoadingStatus>
            ) : promiseSections.length === 0 ? (
              <NoDataStatus>
                <FiCheck size={48} color="#ccc" />
                <p>No promise sections loaded.</p>
                <p>Click "Refresh" or run the seed script to initialize the 3 promise sections.</p>
              </NoDataStatus>
            ) : (
              <PromiseSectionsGrid>
                {promiseSections
                  .sort((a, b) => a.order - b.order)
                  .map((section) => (
                    <PromiseSectionCard key={section._id}>
                      <PromiseSectionHeader>
                        <PromiseSectionOrder>#{section.order}</PromiseSectionOrder>
                        <PromiseSectionStatus isActive={section.isActive}>
                          {section.isActive ? '● Active' : '○ Inactive'}
                        </PromiseSectionStatus>
                      </PromiseSectionHeader>
                      
                      <PromiseSectionVideo>
                        <video 
                          src={section.videoUrl.startsWith('http') 
                            ? section.videoUrl 
                            : section.videoUrl
                          }
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      </PromiseSectionVideo>

                      <PromiseSectionDetails>
                        <PromiseSectionTitle>{section.title}</PromiseSectionTitle>
                        <PromiseSectionLayout>
                          Layout: {section.layout === 'text-left' ? 'Text Left' : 'Text Right'}
                        </PromiseSectionLayout>
                        <PromiseSectionDescription>
                          {section.description}
                        </PromiseSectionDescription>
                        <PromiseSectionActions>
                          <EventActionBtn 
                            variant="edit" 
                            onClick={() => openEditPromiseModal(section)}
                          >
                            <FiEdit2 size={14} /> Edit
                          </EventActionBtn>
                          <EventActionBtn 
                            variant={section.isActive ? 'inactive' : 'active'} 
                            onClick={() => togglePromiseStatus(section)}
                          >
                            {section.isActive ? 'Deactivate' : 'Activate'}
                          </EventActionBtn>
                        </PromiseSectionActions>
                      </PromiseSectionDetails>
                    </PromiseSectionCard>
                  ))}
              </PromiseSectionsGrid>
            )}
          </ContentSection>
        </>
      )}

      {/* Promise Section Modal */}
      {showPromiseModal && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closePromiseModal(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingPromise ? 'Edit Promise Section' : 'Create New Promise Section'}
              </ModalTitle>
              <CloseButton onClick={closePromiseModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <ModalFormGroup>
                <ModalLabel>Title *</ModalLabel>
                <ModalInput
                  type="text"
                  value={promiseForm.title}
                  onChange={(e) => handlePromiseFormChange('title', e.target.value)}
                  placeholder="e.g., Precision, Passion, Perfection"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Description *</ModalLabel>
                <ModalTextarea
                  value={promiseForm.description}
                  onChange={(e) => handlePromiseFormChange('description', e.target.value)}
                  placeholder="Enter the description for this promise section..."
                  rows={6}
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Video URL (Optional)</ModalLabel>
                <ModalInput
                  type="text"
                  value={promiseForm.videoUrl}
                  onChange={(e) => handlePromiseFormChange('videoUrl', e.target.value)}
                  placeholder="https://example.com/video.mp4"
                />
                <ModalHint>Paste a video URL or upload a file below</ModalHint>
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Or Upload Video File *</ModalLabel>
                <FileInputLabel htmlFor="promiseVideoInput">
                  <FiUpload style={{ marginRight: '5px' }} />
                  Choose Video
                </FileInputLabel>
                <FileInput
                  id="promiseVideoInput"
                  type="file"
                  accept="video/*"
                  onChange={handlePromiseVideoChange}
                />
                {promiseVideoFile && (
                  <SelectedFileInfo>
                    Selected: {promiseVideoFile.name}
                  </SelectedFileInfo>
                )}
              </ModalFormGroup>

              <ModalFormRow>
                <ModalFormGroup>
                  <ModalLabel>Layout</ModalLabel>
                  <ModalSelect
                    value={promiseForm.layout}
                    onChange={(e) => handlePromiseFormChange('layout', e.target.value)}
                  >
                    <option value="text-left">Text Left, Video Right</option>
                    <option value="text-right">Video Left, Text Right</option>
                  </ModalSelect>
                </ModalFormGroup>

                <ModalFormGroup>
                  <ModalLabel>Display Order</ModalLabel>
                  <ModalInput
                    type="number"
                    value={promiseForm.order}
                    onChange={(e) => handlePromiseFormChange('order', parseInt(e.target.value))}
                    min="0"
                  />
                  <ModalHint>Lower numbers appear first</ModalHint>
                </ModalFormGroup>
              </ModalFormRow>

              <ModalFormGroup>
                <ModalLabel>Status</ModalLabel>
                <ModalSelect
                  value={promiseForm.isActive}
                  onChange={(e) => handlePromiseFormChange('isActive', e.target.value === 'true')}
                >
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </ModalSelect>
              </ModalFormGroup>
            </ModalBody>
            
            <ModalFooter>
              <ModalButton variant="secondary" onClick={closePromiseModal}>
                Cancel
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={savePromiseSection}
                disabled={promiseSaving}
              >
                {promiseSaving ? 'Saving...' : (editingPromise ? 'Update' : 'Create')}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Hero Videos Tab Content */}
      {activeTab === 'heroVideos' && (
        <>
          <QuickActions>
            <ActionButton 
              variant="primary" 
              onClick={openCreateHeroVideoModal}
            >
              <FiPlus style={{ marginRight: '5px' }} />
              Add New Hero Video
            </ActionButton>
            <ActionButton 
              variant="secondary" 
              onClick={loadHeroVideos}
              disabled={heroVideosLoading}
            >
              <FiRefreshCw style={{ marginRight: '5px' }} />
              {heroVideosLoading ? 'Loading...' : 'Refresh'}
            </ActionButton>
          </QuickActions>

          <ContentSection>
            {heroVideosLoading ? (
              <LoadingStatus>Loading hero videos...</LoadingStatus>
            ) : heroVideos.length === 0 ? (
              <NoDataStatus>
                <FiImage size={48} color="#ccc" />
                <p>No hero videos found.</p>
                <p>Click "Add New Hero Video" to create one.</p>
              </NoDataStatus>
            ) : (
              <HeroVideosGrid>
                {heroVideos
                  .sort((a, b) => a.order - b.order)
                  .map((video) => (
                    <HeroVideoCard key={video._id}>
                      <HeroVideoHeader>
                        <HeroVideoOrder>#{video.order}</HeroVideoOrder>
                        <HeroVideoStatus isActive={video.isActive}>
                          {video.isActive ? '● Active' : '○ Inactive'}
                        </HeroVideoStatus>
                      </HeroVideoHeader>
                      
                      <HeroVideoPlayer>
                        <video 
                          src={video.videoUrl.startsWith('http') 
                            ? video.videoUrl 
                            : video.videoUrl
                          }
                          autoPlay
                          loop
                          muted
                          playsInline
                        />
                      </HeroVideoPlayer>

                      <HeroVideoDetails>
                        <HeroVideoTitle>{video.title}</HeroVideoTitle>
                        <HeroVideoActions>
                          <EventActionBtn 
                            variant="edit" 
                            onClick={() => openEditHeroVideoModal(video)}
                          >
                            <FiEdit2 size={14} /> Edit
                          </EventActionBtn>
                          <EventActionBtn 
                            variant={video.isActive ? 'inactive' : 'active'} 
                            onClick={() => toggleHeroVideoStatus(video)}
                          >
                            {video.isActive ? 'Deactivate' : 'Activate'}
                          </EventActionBtn>
                          <EventActionBtn 
                            variant="delete" 
                            onClick={() => deleteHeroVideo(video._id)}
                          >
                            <FiTrash2 size={14} /> Delete
                          </EventActionBtn>
                        </HeroVideoActions>
                      </HeroVideoDetails>
                    </HeroVideoCard>
                  ))}
              </HeroVideosGrid>
            )}
          </ContentSection>
        </>
      )}

      {/* Deco Slides Tab Content */}
      {activeTab === 'decoSlides' && (
        <>
          <QuickActions>
            <ActionButton onClick={openCreateDecoSlideModal}>
              <FiPlus size={18} /> Add New Deco Slide
            </ActionButton>
            <ActionButton onClick={loadDecoSlides}>
              <FiRefreshCw size={18} /> Refresh
            </ActionButton>
          </QuickActions>

          <ContentSection>
            <SectionHeader>
              <SectionTitle>Deco Carousel Slides</SectionTitle>
              <SectionDescription>
                Manage decoration showcase slides. Add unlimited slides with images, titles, and descriptions.
              </SectionDescription>
            </SectionHeader>

            {decoSlidesLoading ? (
              <LoadingStatus>Loading deco slides...</LoadingStatus>
            ) : decoSlides.length === 0 ? (
              <EmptyState>
                <p>No deco slides found.</p>
                <ActionButton onClick={openCreateDecoSlideModal}>
                  <FiPlus size={18} /> Add First Deco Slide
                </ActionButton>
              </EmptyState>
            ) : (
              <DecoSlidesGrid>
                {decoSlides
                  .sort((a, b) => a.order - b.order)
                  .map((slide) => (
                    <DecoSlideCard key={`deco-${slide._id}-${slide.title}-${slide.imageUrl}`}>
                      <DecoSlideImage 
                        key={`deco-img-${slide._id}-${slide.imageUrl}`}
                        src={slide.imageUrl.startsWith('http') 
                          ? slide.imageUrl 
                          : slide.imageUrl
                        } 
                        alt={slide.title}
                      />
                      <DecoSlideDetails>
                        <DecoSlideTitle>{slide.title}</DecoSlideTitle>
                        <DecoSlideDescription>
                          {slide.description.length > 80 
                            ? `${slide.description.substring(0, 80)}...` 
                            : slide.description
                          }
                        </DecoSlideDescription>
                        <DecoSlideMeta>
                          <DecoSlideOrder>Order: {slide.order}</DecoSlideOrder>
                          <StatusBadge active={slide.isActive}>
                            {slide.isActive ? 'Active' : 'Inactive'}
                          </StatusBadge>
                        </DecoSlideMeta>
                        <DecoSlideActions>
                          <EventActionBtn 
                            variant="edit"
                            onClick={() => openEditDecoSlideModal(slide)}
                          >
                            <FiEdit size={14} /> Edit
                          </EventActionBtn>
                          <EventActionBtn 
                            variant={slide.isActive ? 'warning' : 'success'}
                            onClick={() => toggleDecoSlideStatus(slide)}
                          >
                            {slide.isActive ? (
                              <><FiEyeOff size={14} /> Deactivate</>
                            ) : (
                              <><FiEye size={14} /> Activate</>
                            )}
                          </EventActionBtn>
                          <EventActionBtn 
                            variant="delete"
                            onClick={() => deleteDecoSlide(slide._id)}
                          >
                            <FiTrash2 size={14} /> Delete
                          </EventActionBtn>
                        </DecoSlideActions>
                      </DecoSlideDetails>
                    </DecoSlideCard>
                  ))}
              </DecoSlidesGrid>
            )}
          </ContentSection>
        </>
      )}

      {/* Catering Cards Tab Content */}
      {activeTab === 'cateringCards' && (
        <>
          <QuickActions>
            <ActionButton onClick={openCreateCateringCardModal}>
              <FiPlus size={18} /> Add New Catering Card
            </ActionButton>
            <ActionButton onClick={loadCateringCards}>
              <FiRefreshCw size={18} /> Refresh
            </ActionButton>
          </QuickActions>

          <ContentSection>
            <SectionHeader>
              <SectionTitle>Catering Menu Cards</SectionTitle>
              <SectionDescription>
                Manage catering cards with videos, titles, and descriptions. Each card displays cuisine type with associated content.
              </SectionDescription>
            </SectionHeader>

            {cateringCardsLoading ? (
              <LoadingStatus>Loading catering cards...</LoadingStatus>
            ) : cateringCards.length === 0 ? (
              <EmptyState>
                <p>No catering cards found.</p>
                <ActionButton onClick={openCreateCateringCardModal}>
                  <FiPlus size={18} /> Add First Catering Card
                </ActionButton>
              </EmptyState>
            ) : (
              <CateringCardsGrid>
                {cateringCards
                  .sort((a, b) => a.order - b.order)
                  .map((card) => (
                    <CateringCardItem key={`catering-${card._id}-${card.name}-${card.videoUrl}`}>
                      <CateringCardHeader>
                        <CateringCardName>{card.name}</CateringCardName>
                        <CateringCardMeta>
                          <CateringCardOrder>Order: {card.order}</CateringCardOrder>
                          <StatusBadge active={card.isActive}>
                            {card.isActive ? 'Active' : 'Inactive'}
                          </StatusBadge>
                        </CateringCardMeta>
                      </CateringCardHeader>
                      
                      <CateringCardBody>
                        <CateringCardDescription>{card.description}</CateringCardDescription>
                        <CateringCardTitle>{card.title}</CateringCardTitle>
                        <CateringCardSubtitle>
                          {card.subtitle.length > 100 
                            ? `${card.subtitle.substring(0, 100)}...` 
                            : card.subtitle
                          }
                        </CateringCardSubtitle>
                      </CateringCardBody>
                      
                      <CateringCardActions>
                        <EventActionBtn 
                          variant="edit"
                          onClick={() => openEditCateringCardModal(card)}
                        >
                          <FiEdit size={14} /> Edit
                        </EventActionBtn>
                        <EventActionBtn 
                          variant={card.isActive ? 'warning' : 'success'}
                          onClick={() => toggleCateringCardStatus(card)}
                        >
                          {card.isActive ? (
                            <><FiEyeOff size={14} /> Deactivate</>
                          ) : (
                            <><FiEye size={14} /> Activate</>
                          )}
                        </EventActionBtn>
                        <EventActionBtn 
                          variant="delete"
                          onClick={() => deleteCateringCard(card._id)}
                        >
                          <FiTrash2 size={14} /> Delete
                        </EventActionBtn>
                      </CateringCardActions>
                    </CateringCardItem>
                  ))}
              </CateringCardsGrid>
            )}
          </ContentSection>
        </>
      )}

      {/* Hero Video Modal */}
      {showHeroVideoModal && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closeHeroVideoModal(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingHeroVideo ? 'Edit Hero Video' : 'Create New Hero Video'}
              </ModalTitle>
              <CloseButton onClick={closeHeroVideoModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <ModalFormGroup>
                <ModalLabel>Title *</ModalLabel>
                <ModalInput
                  type="text"
                  value={heroVideoForm.title}
                  onChange={(e) => handleHeroVideoFormChange('title', e.target.value)}
                  placeholder="e.g., Wedding Events, Corporate Events"
                />
              </ModalFormGroup>

              {/* Video Preview */}
              {(editingHeroVideo?.videoUrl || heroVideoForm.videoUrl) && !heroVideoFile && (
                <ModalFormGroup>
                  <ModalLabel>Current Video Preview</ModalLabel>
                  <VideoPreviewContainer>
                    <video 
                      src={(editingHeroVideo?.videoUrl || heroVideoForm.videoUrl).startsWith('http') 
                        ? (editingHeroVideo?.videoUrl || heroVideoForm.videoUrl)
                        : editingHeroVideo?.videoUrl || heroVideoForm.videoUrl
                      }
                      controls
                      style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
                    />
                  </VideoPreviewContainer>
                </ModalFormGroup>
              )}

              <ModalFormGroup>
                <ModalLabel>Video URL (Optional)</ModalLabel>
                <ModalInput
                  type="text"
                  value={heroVideoForm.videoUrl}
                  onChange={(e) => handleHeroVideoFormChange('videoUrl', e.target.value)}
                  placeholder="https://example.com/video.mp4"
                />
                <ModalHint>Paste a video URL or upload a file below</ModalHint>
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Or Upload Video File *</ModalLabel>
                <FileInputLabel htmlFor="heroVideoInput">
                  <FiUpload style={{ marginRight: '5px' }} />
                  Choose Video
                </FileInputLabel>
                <FileInput
                  id="heroVideoInput"
                  type="file"
                  accept="video/*"
                  onChange={handleHeroVideoFileChange}
                />
                {heroVideoFile && (
                  <SelectedFileInfo>
                    Selected: {heroVideoFile.name}
                  </SelectedFileInfo>
                )}
              </ModalFormGroup>

              <ModalFormRow>
                <ModalFormGroup>
                  <ModalLabel>Display Order</ModalLabel>
                  <ModalInput
                    type="number"
                    value={heroVideoForm.order}
                    onChange={(e) => handleHeroVideoFormChange('order', parseInt(e.target.value))}
                    min="0"
                  />
                  <ModalHint>Lower numbers appear first</ModalHint>
                </ModalFormGroup>

                <ModalFormGroup>
                  <ModalLabel>Status</ModalLabel>
                  <ModalSelect
                    value={heroVideoForm.isActive}
                    onChange={(e) => handleHeroVideoFormChange('isActive', e.target.value === 'true')}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </ModalSelect>
                </ModalFormGroup>
              </ModalFormRow>
            </ModalBody>
            
            <ModalFooter>
              <ModalButton variant="secondary" onClick={closeHeroVideoModal}>
                Cancel
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={saveHeroVideo}
                disabled={heroVideoSaving}
              >
                {heroVideoSaving ? 'Saving...' : (editingHeroVideo ? 'Update' : 'Create')}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Deco Slide Modal */}
      {showDecoSlideModal && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closeDecoSlideModal(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingDecoSlide ? 'Edit Deco Slide' : 'Create New Deco Slide'}
              </ModalTitle>
              <CloseButton onClick={closeDecoSlideModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <ModalFormGroup>
                <ModalLabel>Title *</ModalLabel>
                <ModalInput
                  type="text"
                  value={decoSlideForm.title}
                  onChange={(e) => handleDecoSlideFormChange('title', e.target.value)}
                  placeholder="e.g., Elegant Table Settings"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Description *</ModalLabel>
                <ModalTextarea
                  value={decoSlideForm.description}
                  onChange={(e) => handleDecoSlideFormChange('description', e.target.value)}
                  placeholder="Enter slide description..."
                  rows={4}
                />
              </ModalFormGroup>

              {/* Image Preview */}
              {decoSlideImagePreview && (
                <ModalFormGroup>
                  <ModalLabel>Image Preview</ModalLabel>
                  <DecoImagePreview>
                    <img 
                      key={`deco-preview-${decoSlideImagePreview}`}
                      src={decoSlideImagePreview} 
                      alt="Preview"
                      style={{ width: '100%', maxHeight: '300px', objectFit: 'cover', borderRadius: '8px' }}
                    />
                  </DecoImagePreview>
                </ModalFormGroup>
              )}

              <ModalFormGroup>
                <ModalLabel>Image URL</ModalLabel>
                <ModalInput
                  type="text"
                  value={decoSlideForm.imageUrl}
                  onChange={(e) => handleDecoSlideFormChange('imageUrl', e.target.value)}
                  placeholder="https://example.com/image.jpg (or upload below)"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Or Upload Image</ModalLabel>
                <FileInputWrapper>
                  <FileInput
                    id="decoSlideImageUpload"
                    type="file"
                    accept="image/*"
                    onChange={handleDecoSlideImageChange}
                  />
                  <FileInputLabel htmlFor="decoSlideImageUpload">
                    <FiUpload style={{ marginRight: '8px' }} />
                    {decoSlideImageFile ? decoSlideImageFile.name : 'Choose Image File'}
                  </FileInputLabel>
                </FileInputWrapper>
                <HelperText>Supports: JPG, PNG, GIF, WebP (Max 10MB)</HelperText>
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Display Order</ModalLabel>
                <ModalInput
                  type="number"
                  value={decoSlideForm.order}
                  onChange={(e) => handleDecoSlideFormChange('order', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Status</ModalLabel>
                <ModalSelect
                  value={decoSlideForm.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleDecoSlideFormChange('isActive', e.target.value === 'active')}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </ModalSelect>
              </ModalFormGroup>
            </ModalBody>
            
            <ModalFooter>
              <ModalButton 
                variant="secondary" 
                onClick={closeDecoSlideModal}
              >
                Cancel
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={saveDecoSlide}
                disabled={decoSlideSaving}
              >
                {decoSlideSaving ? 'Saving...' : (editingDecoSlide ? 'Update' : 'Create')}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Catering Card Modal */}
      {showCateringCardModal && (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closeCateringCardModal(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingCateringCard ? 'Edit Catering Card' : 'Create New Catering Card'}
              </ModalTitle>
              <CloseButton onClick={closeCateringCardModal}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              <ModalFormGroup>
                <ModalLabel>Card Name * (e.g., "Italian", "Asian Fusion")</ModalLabel>
                <ModalInput
                  type="text"
                  value={cateringCardForm.name}
                  onChange={(e) => handleCateringCardFormChange('name', e.target.value)}
                  placeholder="e.g., Italian"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Card Description * (shown on card)</ModalLabel>
                <ModalInput
                  type="text"
                  value={cateringCardForm.description}
                  onChange={(e) => handleCateringCardFormChange('description', e.target.value)}
                  placeholder="e.g., Fresh pasta, wood-fired pizza, traditional recipes"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Title * (shown when card is selected)</ModalLabel>
                <ModalInput
                  type="text"
                  value={cateringCardForm.title}
                  onChange={(e) => handleCateringCardFormChange('title', e.target.value)}
                  placeholder="e.g., Authentic Italian Cuisine"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Subtitle * (detailed description when card is selected)</ModalLabel>
                <ModalTextarea
                  value={cateringCardForm.subtitle}
                  onChange={(e) => handleCateringCardFormChange('subtitle', e.target.value)}
                  placeholder="Experience the finest Italian culinary artistry..."
                  rows="3"
                />
              </ModalFormGroup>

              {/* Video Preview */}
              {(editingCateringCard?.videoUrl || cateringCardForm.videoUrl) && !cateringCardVideoFile && (
                <ModalFormGroup>
                  <ModalLabel>Current Video Preview</ModalLabel>
                  <VideoPreviewContainer>
                    <video 
                      key={`catering-video-${editingCateringCard?.videoUrl || cateringCardForm.videoUrl}`}
                      src={(editingCateringCard?.videoUrl || cateringCardForm.videoUrl).startsWith('http') 
                        ? (editingCateringCard?.videoUrl || cateringCardForm.videoUrl)
                        : editingCateringCard?.videoUrl || cateringCardForm.videoUrl
                      }
                      controls
                      style={{ width: '100%', maxHeight: '300px', borderRadius: '8px' }}
                    />
                  </VideoPreviewContainer>
                </ModalFormGroup>
              )}

              <ModalFormGroup>
                <ModalLabel>Video URL (or upload below)</ModalLabel>
                <ModalInput
                  type="url"
                  value={cateringCardForm.videoUrl}
                  onChange={(e) => handleCateringCardFormChange('videoUrl', e.target.value)}
                  placeholder="https://example.com/video.mp4"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Or Upload Video File</ModalLabel>
                <FileInputWrapper>
                  <FileInput
                    id="cateringCardVideoUpload"
                    type="file"
                    accept="video/mp4,video/mov,video/avi,video/webm"
                    onChange={handleCateringCardVideoChange}
                  />
                  <FileInputLabel htmlFor="cateringCardVideoUpload">
                    <FiUpload style={{ marginRight: '8px' }} />
                    {cateringCardVideoFile ? cateringCardVideoFile.name : 'Choose Video File'}
                  </FileInputLabel>
                </FileInputWrapper>
                {cateringCardVideoFile && (
                  <HelperText>Selected: {cateringCardVideoFile.name}</HelperText>
                )}
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Order</ModalLabel>
                <ModalInput
                  type="number"
                  value={cateringCardForm.order}
                  onChange={(e) => handleCateringCardFormChange('order', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </ModalFormGroup>

              <ModalFormGroup>
                <ModalLabel>Status</ModalLabel>
                <select
                  value={cateringCardForm.isActive ? 'active' : 'inactive'}
                  onChange={(e) => handleCateringCardFormChange('isActive', e.target.value === 'active')}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </ModalFormGroup>
            </ModalBody>
            
            <ModalFooter>
              <ModalButton variant="secondary" onClick={closeCateringCardModal}>
                Cancel
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={saveCateringCard}
                disabled={cateringCardSaving}
              >
                {cateringCardSaving ? 'Saving...' : (editingCateringCard ? 'Update' : 'Create')}
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}

      {/* Event Type Modal */}
      {showEventModal && previewMode && (
        <>
          <FullScreenPreview>
            <PreviewHeader>
              <PreviewTitle>
                {editingEvent ? 'Edit Event: ' + eventForm.name : 'Create New Event'}
              </PreviewTitle>
              <PreviewActions>
                <PreviewActionButton onClick={handleSaveDraft}>
                  <FiSave /> Save Draft
                </PreviewActionButton>
                <AddSectionDropdown>
                  <DropdownButton onClick={() => setShowAddSectionMenu(!showAddSectionMenu)}>
                    <FiPlus /> Add Section <FiChevronDown />
                  </DropdownButton>
                  {showAddSectionMenu && (
                    <DropdownMenu>
                      <DropdownItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSection('fazyraTouch');
                        }}
                      >
                        {eventForm.fazyraTouch?.enabled ? '✓ ' : ''}Fazyra Touch
                      </DropdownItem>
                      <DropdownItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSection('highlights');
                        }}
                      >
                        {eventForm.highlights?.enabled ? '✓ ' : ''}Event Highlights
                      </DropdownItem>
                      <DropdownItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSection('helpYouPlan');
                        }}
                      >
                        {eventForm.helpYouPlan?.enabled ? '✓ ' : ''}What We Can Help You Plan
                      </DropdownItem>
                      <DropdownItem 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAddSection('dynamicSections');
                        }}
                      >
                        Dynamic Sections
                      </DropdownItem>
                    </DropdownMenu>
                  )}
                </AddSectionDropdown>
                <ClosePreviewButton onClick={closeEventModal}>
                  <FiX /> Close
                </ClosePreviewButton>
              </PreviewActions>
            </PreviewHeader>
            
            <PreviewContent>
              <EventPreview 
                eventData={eventForm}
                onEdit={handleEditSection}
                onDelete={handleDeleteSection}
              />
            </PreviewContent>

            <PreviewFooter>
              <FooterButton variant="secondary" onClick={closeEventModal}>
                Cancel
              </FooterButton>
              <FooterButton 
                variant="primary" 
                onClick={saveEvent}
                disabled={eventSaving}
              >
                {eventSaving ? 'Publishing...' : (editingEvent ? 'Update & Publish' : 'Publish Event')}
              </FooterButton>
            </PreviewFooter>
          </FullScreenPreview>

          {/* Section Editor Modal - Shows over preview */}
          {editingSection && (() => {
            console.log('Rendering section editor modal for:', editingSection);
            return (
        <Modal onClick={(e) => { if (e.target === e.currentTarget) closeSectionEditor(); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                {editingSection === 'basic' ? 'Edit Basic Information' :
                 editingSection === 'fazyraTouch' ? 'Edit Fazyra Touch Section' :
                 editingSection === 'highlights' ? 'Edit Event Highlights' :
                 editingSection === 'dynamicSections' ? 'Edit Dynamic Sections' :
                 'Edit Help You Plan Section'}
              </ModalTitle>
              <CloseButton onClick={closeSectionEditor}>
                <FiX />
              </CloseButton>
            </ModalHeader>
            
            <ModalBody>
              {editingSection === 'basic' && (
                <>
                  <ModalFormGroup>
                    <ModalLabel>Event Name *</ModalLabel>
                    <ModalInput
                      type="text"
                      value={eventForm.name}
                      onChange={(e) => handleEventFormChange('name', e.target.value)}
                      placeholder="e.g., Wedding, Corporate, Birthday"
                    />
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <ModalLabel>Description</ModalLabel>
                    <ModalTextarea
                      value={eventForm.description}
                      onChange={(e) => handleEventFormChange('description', e.target.value)}
                      placeholder="Brief description of this event type..."
                      rows={3}
                    />
                  </ModalFormGroup>

                  {eventImagePreview && (
                    <ImagePreviewContainer>
                      <ModalLabel>Image Preview:</ModalLabel>
                      <ImagePreview>
                        <img 
                          src={eventImagePreview.startsWith('http') || eventImagePreview.startsWith('data:')
                            ? eventImagePreview 
                            : eventImagePreview
                          } 
                          alt="Preview"
                          onError={(e) => e.target.src = 'https://via.placeholder.com/400x300?text=Invalid+Image'}
                        />
                      </ImagePreview>
                    </ImagePreviewContainer>
                  )}

                  <ModalFormGroup>
                    <ModalLabel>Poster Image URL (Optional)</ModalLabel>
                    <ModalInput
                      type="text"
                      value={eventForm.posterImage}
                      onChange={(e) => handleEventFormChange('posterImage', e.target.value)}
                      placeholder="https://images.unsplash.com/photo-..."
                    />
                    <ModalHint>Paste an image URL (Unsplash recommended) or upload a file below</ModalHint>
                  </ModalFormGroup>

                  <ModalFormGroup>
                    <ModalLabel>Or Upload Image File</ModalLabel>
                    <FileInputLabel htmlFor="eventImageInput">
                      <FiUpload style={{ marginRight: '5px' }} />
                      Choose Image
                    </FileInputLabel>
                    <FileInput
                      id="eventImageInput"
                      type="file"
                      accept="image/*"
                      onChange={handleEventImageChange}
                    />
                    {eventImageFile && (
                      <SelectedFileInfo>
                        Selected: {eventImageFile.name}
                      </SelectedFileInfo>
                    )}
                  </ModalFormGroup>

                  <ModalFormRow>
                    <ModalFormGroup>
                      <ModalLabel>Display Order</ModalLabel>
                      <ModalInput
                        type="number"
                        value={eventForm.order}
                        onChange={(e) => handleEventFormChange('order', parseInt(e.target.value))}
                        min="0"
                      />
                      <ModalHint>Lower numbers appear first</ModalHint>
                    </ModalFormGroup>

                    <ModalFormGroup>
                      <ModalLabel>Status</ModalLabel>
                      <ModalSelect
                        value={eventForm.isActive}
                        onChange={(e) => handleEventFormChange('isActive', e.target.value === 'true')}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </ModalSelect>
                    </ModalFormGroup>
                  </ModalFormRow>
                </>
              )}

              {editingSection === 'fazyraTouch' && (
                <FazyraTouchForm
                  fazyraTouch={eventForm.fazyraTouch}
                  onChange={handleEventFormChange}
                  onImageChange={handleFazyraTouchImageChange}
                />
              )}

              {editingSection === 'highlights' && (
                <EventHighlightsForm
                  highlights={eventForm.highlights || { enabled: false, title: 'Event Highlights', subtitle: 'Creating Moments That Last Forever', cards: [] }}
                  onChange={handleEventFormChange}
                />
              )}

              {editingSection === 'helpYouPlan' && (
                <HelpYouPlanForm
                  helpYouPlan={eventForm.helpYouPlan || { enabled: false, sectionHeading: 'What We Can Help You Plan', cards: [] }}
                  onChange={handleEventFormChange}
                  onVideoFileChange={handleHelpYouPlanVideoChange}
                  videoPreviews={helpYouPlanVideoPreviews}
                />
              )}

              {editingSection === 'dynamicSections' && (
                <DynamicSectionsForm
                  dynamicSections={eventForm.dynamicSections || []}
                  onChange={handleEventFormChange}
                  onImageChange={handleDynamicSectionImageChange}
                />
              )}
            </ModalBody>
            
            <ModalFooter>
              <ModalButton variant="secondary" onClick={closeSectionEditor}>
                Close
              </ModalButton>
              <ModalButton 
                variant="primary" 
                onClick={closeSectionEditor}
              >
                Save Changes
              </ModalButton>
            </ModalFooter>
          </ModalContent>
        </Modal>
            );
          })()}
        </>
      )}
    </PageContainer>
  );
};

// Styled Components
const PageContainer = styled.div`
  padding: 32px;
  max-width: 1600px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  min-height: 100vh;
  position: relative;
  
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at 20% 50%, rgba(0, 255, 200, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 80% 80%, rgba(0, 150, 255, 0.03) 0%, transparent 50%),
                radial-gradient(circle at 40% 20%, rgba(255, 0, 150, 0.02) 0%, transparent 50%);
    pointer-events: none;
    z-index: 0;
  }
  
  > * {
    position: relative;
    z-index: 1;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 40px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  padding: 32px;
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: linear-gradient(90deg, 
      transparent,
      rgba(0, 255, 200, 0.4),
      rgba(0, 150, 255, 0.4),
      transparent
    );
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  background: linear-gradient(135deg, #00ffc8 0%, #00a3ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  letter-spacing: -0.5px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  font-weight: 400;
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 24px;
  font-weight: 500;
  
  ${props => props.type === 'success' && `
    background: #d4edda;
    border: 1px solid #c3e6cb;
    color: #155724;
  `}
  
  ${props => props.type === 'error' && `
    background: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
  `}
  
  ${props => props.type === 'info' && `
    background: #d1ecf1;
    border: 1px solid #bee5eb;
    color: #0c5460;
  `}
`;

const QuickActions = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 32px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  border: none;
  border-radius: 16px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  color: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.2), rgba(0, 150, 255, 0.2));
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover:not(:disabled)::before {
    width: 300px;
    height: 300px;
  }
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 255, 200, 0.15);
    border-color: rgba(0, 255, 200, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  svg, span {
    position: relative;
    z-index: 1;
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const ContentSection = styled.div`
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4),
              inset 0 1px 0 rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const LoadingStatus = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  font-weight: 500;
`;

const NoDataStatus = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: rgba(255, 255, 255, 0.4);
  
  p {
    margin: 12px 0;
    font-size: 16px;
  }
  
  p:first-of-type {
    font-weight: 700;
    color: #00ffc8;
    font-size: 18px;
  }
`;

const EventTypesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
`;

const EventTypeCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, 
      rgba(0, 255, 200, 0.05) 0%, 
      rgba(0, 150, 255, 0.05) 100%
    );
    opacity: 0;
    transition: opacity 0.4s;
  }
  
  &:hover {
    box-shadow: 0 16px 48px rgba(0, 255, 200, 0.15);
    transform: translateY(-8px);
    border-color: rgba(0, 255, 200, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
`;

const EventTypeImage = styled.div`
  position: relative;
  width: 100%;
  height: 220px;
  background: rgba(0, 0, 0, 0.4);
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover img {
    transform: scale(1.1);
  }
`;

const NoImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, 
    rgba(0, 255, 200, 0.1) 0%, 
    rgba(0, 150, 255, 0.1) 100%
  );
  color: rgba(255, 255, 255, 0.4);
  gap: 12px;
  
  span {
    font-size: 14px;
    font-weight: 500;
  }
`;

const EventTypeOrder = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  color: #00ffc8;
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  border: 1px solid rgba(0, 255, 200, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const EventTypeStatus = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  background: ${props => props.isActive 
    ? 'rgba(0, 255, 200, 0.15)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  backdrop-filter: blur(10px);
  color: ${props => props.isActive ? '#00ffc8' : 'rgba(255, 255, 255, 0.6)'};
  padding: 8px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 6px;
  border: 1px solid ${props => props.isActive 
    ? 'rgba(0, 255, 200, 0.3)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
`;

const EventTypeDetails = styled.div`
  padding: 24px;
  background: rgba(0, 0, 0, 0.2);
`;

const EventTypeName = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const EventTypeSlug = styled.div`
  font-size: 13px;
  color: #007bff;
  font-family: 'Courier New', monospace;
  margin-bottom: 12px;
`;

const EventTypeDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const EventTypeActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const EventActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  border: 1px solid;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  ${props => props.variant === 'edit' && `
    background: rgba(0, 163, 255, 0.15);
    color: #00a3ff;
    border-color: rgba(0, 163, 255, 0.3);
    
    &::before {
      background: rgba(0, 163, 255, 0.3);
    }
    
    &:hover {
      background: rgba(0, 163, 255, 0.25);
      border-color: rgba(0, 163, 255, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0, 163, 255, 0.2);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
  
  ${props => props.variant === 'active' && `
    background: rgba(0, 255, 200, 0.15);
    color: #00ffc8;
    border-color: rgba(0, 255, 200, 0.3);
    
    &::before {
      background: rgba(0, 255, 200, 0.3);
    }
    
    &:hover {
      background: rgba(0, 255, 200, 0.25);
      border-color: rgba(0, 255, 200, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(0, 255, 200, 0.2);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
  
  ${props => props.variant === 'inactive' && `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.6);
    border-color: rgba(255, 255, 255, 0.1);
    
    &::before {
      background: rgba(255, 255, 255, 0.1);
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.2);
      color: rgba(255, 255, 255, 0.8);
      transform: translateY(-2px);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
  
  ${props => props.variant === 'delete' && `
    background: rgba(255, 50, 80, 0.15);
    color: #ff5080;
    border-color: rgba(255, 50, 80, 0.3);
    
    &::before {
      background: rgba(255, 50, 80, 0.3);
    }
    
    &:hover {
      background: rgba(255, 50, 80, 0.25);
      border-color: rgba(255, 50, 80, 0.5);
      transform: translateY(-2px);
      box-shadow: 0 8px 16px rgba(255, 50, 80, 0.2);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
`;

// Modal Styles
const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 20000;
  padding: 20px;
  animation: fadeIn 0.3s ease;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const ModalContent = styled.div`
  background: rgba(10, 10, 10, 0.95);
  backdrop-filter: blur(30px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5), 0 0 40px rgba(0, 255, 200, 0.1);
  animation: slideUp 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  
  @keyframes slideUp {
    from {
      transform: translateY(30px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.4);
`;

const ModalTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #00ffc8 0%, #00a3ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-shadow: 0 0 20px rgba(0, 255, 200, 0.3);
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  padding: 10px;
  color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #00ffc8;
    border-color: rgba(0, 255, 200, 0.3);
    transform: rotate(90deg);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const ModalBody = styled.div`
  padding: 28px;
  background: rgba(0, 0, 0, 0.2);
`;

const ModalFormGroup = styled.div`
  margin-bottom: 24px;
`;

const ModalLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #00ffc8;
  margin-bottom: 10px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 14px;
  color: #ffffff;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus {
    outline: none;
    border-color: rgba(0, 255, 200, 0.5);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 20px rgba(0, 255, 200, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  color: #ffffff;
  resize: vertical;
  min-height: 100px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus {
    outline: none;
    border-color: rgba(0, 255, 200, 0.5);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 20px rgba(0, 255, 200, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const ModalSelect = styled.select`
  width: 100%;
  padding: 14px 16px;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  font-size: 14px;
  color: #ffffff;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:focus {
    outline: none;
    border-color: rgba(0, 255, 200, 0.5);
    background: rgba(255, 255, 255, 0.08);
    box-shadow: 0 0 20px rgba(0, 255, 200, 0.1);
  }
  
  option {
    background: #1a1a1a;
    color: #ffffff;
  }
`;

const ModalHint = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 8px;
  font-style: italic;
`;

const ModalFormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 14px 24px;
  background: linear-gradient(135deg, rgba(0, 255, 200, 0.15) 0%, rgba(0, 163, 255, 0.15) 100%);
  backdrop-filter: blur(10px);
  color: #00ffc8;
  border: 1px solid rgba(0, 255, 200, 0.3);
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(0, 255, 200, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.25) 0%, rgba(0, 163, 255, 0.25) 100%);
    border-color: rgba(0, 255, 200, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 255, 200, 0.2);
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }
`;

const SelectedFileInfo = styled.div`
  font-size: 13px;
  color: #00ffc8;
  font-weight: 600;
  padding: 12px 16px;
  background: rgba(0, 255, 200, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 10px;
  border: 1px solid rgba(0, 255, 200, 0.3);
  margin-top: 12px;
  box-shadow: 0 4px 12px rgba(0, 255, 200, 0.1);
`;

const ImagePreviewContainer = styled.div`
  margin-bottom: 24px;
`;

const ImagePreview = styled.div`
  width: 100%;
  max-height: 300px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(0, 255, 200, 0.3);
  background: rgba(0, 0, 0, 0.4);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  }
  
  &:hover img {
    transform: scale(1.05);
  }
`;

const VideoPreviewContainer = styled.div`
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(0, 255, 200, 0.3);
  background: #000;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
`;

const ModalFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 24px 28px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(0, 0, 0, 0.4);
`;

const ModalButton = styled.button`
  padding: 12px 28px;
  border: 1px solid;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(10px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.2) 0%, rgba(0, 163, 255, 0.2) 100%);
    color: #00ffc8;
    border-color: rgba(0, 255, 200, 0.4);
    
    &::before {
      background: rgba(0, 255, 200, 0.3);
    }
    
    &:hover:not(:disabled) {
      background: linear-gradient(135deg, rgba(0, 255, 200, 0.3) 0%, rgba(0, 163, 255, 0.3) 100%);
      border-color: rgba(0, 255, 200, 0.6);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(0, 255, 200, 0.3);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.8);
    border-color: rgba(255, 255, 255, 0.2);
    
    &::before {
      background: rgba(255, 255, 255, 0.1);
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
      
      &::before {
        width: 300px;
        height: 300px;
      }
    }
  `}
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none !important;
  }
`;

// Preview Mode Styled Components
const FullScreenPreview = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 100%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
`;

const PreviewHeader = styled.div`
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(20px);
  padding: 20px 28px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
  position: relative;
  z-index: 20001; /* Higher than modal content to keep header on top */
`;

const PreviewTitle = styled.h2`
  font-size: 22px;
  font-weight: 700;
  background: linear-gradient(135deg, #00ffc8 0%, #00a3ff 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0;
  text-shadow: 0 0 20px rgba(0, 255, 200, 0.3);
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  position: relative;
  z-index: 30000; /* Ensure actions are above everything */
`;

const PreviewActionButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, rgba(0, 255, 200, 0.2) 0%, rgba(0, 163, 255, 0.2) 100%);
  backdrop-filter: blur(10px);
  color: #00ffc8;
  border: 1px solid rgba(0, 255, 200, 0.4);
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(0, 255, 200, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.6s, height 0.6s;
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.3) 0%, rgba(0, 163, 255, 0.3) 100%);
    border-color: rgba(0, 255, 200, 0.6);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 255, 200, 0.3);
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }
`;

const AddSectionDropdown = styled.div`
  position: relative;
  z-index: 1; /* Relative within PreviewActions */
`;

const DropdownButton = styled.button`
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  pointer-events: all; /* Ensure button is clickable */
  
  &:hover {
    background: #2563eb;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1; /* Within PreviewActions stacking context */
  overflow: hidden;
  pointer-events: all; /* Ensure menu is clickable */
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: white;
  border: none;
  text-align: left;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  pointer-events: all; /* Ensure items are clickable */
  color: #1f2937; /* Dark text for visibility */
  font-weight: 500;
  
  &:hover {
    background: #3b82f6;
    color: white;
    transform: translateX(4px);
  }
  
  &:active {
    background: #2563eb;
  }
`;

const ClosePreviewButton = styled.button`
  padding: 10px 20px;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  
  &:hover {
    background: #dc2626;
  }
`;

const PreviewContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;
  width: 100%;
`;

const PreviewFooter = styled.div`
  background: white;
  padding: 16px 24px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.05);
`;

const FooterButton = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' && `
    background: #3b82f6;
    color: white;
    
    &:hover:not(:disabled) {
      background: #2563eb;
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: #e5e7eb;
    color: #374151;
    
    &:hover {
      background: #d1d5db;
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

// Tabs Styled Components
const TabsContainer = styled.div`
  display: flex;
  gap: 12px;
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  margin-bottom: 32px;
  padding: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const Tab = styled.button`
  flex: 1;
  padding: 16px 24px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, rgba(0, 255, 200, 0.15) 0%, rgba(0, 150, 255, 0.15) 100%)'
    : 'transparent'
  };
  color: ${props => props.active ? '#00ffc8' : 'rgba(255, 255, 255, 0.6)'};
  border: ${props => props.active 
    ? '1px solid rgba(0, 255, 200, 0.3)'
    : '1px solid transparent'
  };
  border-radius: 14px;
  font-size: 15px;
  font-weight: ${props => props.active ? '700' : '500'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.1), rgba(0, 150, 255, 0.1));
    opacity: 0;
    transition: opacity 0.3s;
  }

  &:hover {
    background: ${props => props.active 
      ? 'linear-gradient(135deg, rgba(0, 255, 200, 0.2) 0%, rgba(0, 150, 255, 0.2) 100%)'
      : 'rgba(255, 255, 255, 0.05)'
    };
    transform: translateY(-2px);
    
    &::before {
      opacity: 1;
    }
  }
  
  &:active {
    transform: translateY(0);
  }
`;

// Description Tab Styled Components
const DescriptionContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const DescriptionCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.5rem;
`;

const DescriptionFormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const DescriptionLabel = styled.label`
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 0.5rem;
  font-size: 1.1rem;
`;

const DescriptionHint = styled.p`
  color: #666;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  font-style: italic;
`;

const DescriptionTextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 1rem;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #007bff;
  }

  &::placeholder {
    color: #999;
  }
`;

const CharacterCount = styled.div`
  text-align: right;
  font-size: 0.85rem;
  color: ${props => props.warning ? '#ff6b6b' : '#666'};
  margin-top: 0.5rem;
`;

const SaveDescriptionButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: #007bff;
  color: white;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #0056b3;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
  }
`;

const PreviewSection = styled.div`
  margin-top: 0;
`;

const DescriptionPreviewTitle = styled.h3`
  font-size: 1.2rem;
  color: #333;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PreviewBox = styled.div`
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
`;

const PreviewText = styled.p`
  font-size: 1.05rem;
  line-height: 1.6;
  color: #333;
  white-space: pre-line;
`;

// Promise Sections Styled Components
const PromiseSectionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
`;

const PromiseSectionCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
  }
`;

const PromiseSectionHeader = styled.div`
  position: relative;
  padding: 12px;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PromiseSectionOrder = styled.div`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const PromiseSectionStatus = styled.div`
  background: ${props => props.isActive ? 'rgba(40, 167, 69, 0.9)' : 'rgba(108, 117, 125, 0.9)'};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const PromiseSectionVideo = styled.div`
  width: 100%;
  height: 250px;
  background: #000;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PromiseSectionDetails = styled.div`
  padding: 20px;
`;

const PromiseSectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 8px 0;
`;

const PromiseSectionLayout = styled.div`
  font-size: 13px;
  color: #007bff;
  font-family: 'Courier New', monospace;
  margin-bottom: 12px;
`;

const PromiseSectionDescription = styled.p`
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  margin: 0 0 16px 0;
  display: -webkit-box;
  -webkit-line-clamp: 4;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PromiseSectionActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// Hero Videos Styled Components
const HeroVideosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
`;

const HeroVideoCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-4px);
  }
`;

const HeroVideoHeader = styled.div`
  position: relative;
  padding: 12px;
  background: #f8f9fa;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeroVideoOrder = styled.div`
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
`;

const HeroVideoStatus = styled.div`
  background: ${props => props.isActive ? 'rgba(40, 167, 69, 0.9)' : 'rgba(108, 117, 125, 0.9)'};
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const HeroVideoPlayer = styled.div`
  width: 100%;
  height: 250px;
  background: #000;
  
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const HeroVideoDetails = styled.div`
  padding: 20px;
`;

const HeroVideoTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 16px 0;
`;

const HeroVideoActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

// Section Header Styled Components
const SectionHeader = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 8px 0;
`;

const SectionDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  margin: 0;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  background: rgba(255, 255, 255, 0.02);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 2px dashed rgba(0, 255, 200, 0.2);
  
  p {
    color: rgba(255, 255, 255, 0.5);
    font-size: 1rem;
    margin-bottom: 20px;
    font-weight: 500;
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 6px 14px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${props => props.active 
    ? 'rgba(0, 255, 200, 0.15)' 
    : 'rgba(255, 255, 255, 0.1)'
  };
  color: ${props => props.active ? '#00ffc8' : 'rgba(255, 255, 255, 0.6)'};
  border: 1px solid ${props => props.active 
    ? 'rgba(0, 255, 200, 0.3)' 
    : 'rgba(255, 255, 255, 0.2)'
  };
`;

const FileInputWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const HelperText = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.5);
  margin: 6px 0 0 0;
  font-style: italic;
`;

// Deco Slides Styled Components
const DecoSlidesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 24px;
`;

const DecoSlideCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.08);
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.05), rgba(0, 163, 255, 0.05));
    opacity: 0;
    transition: opacity 0.5s;
    pointer-events: none;
  }
  
  &:hover {
    box-shadow: 0 16px 48px rgba(0, 255, 200, 0.15);
    transform: translateY(-8px);
    border-color: rgba(0, 255, 200, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
`;

const DecoSlideImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${DecoSlideCard}:hover & {
    transform: scale(1.05);
  }
`;

const DecoSlideDetails = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.3);
`;

const DecoSlideTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const DecoSlideDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.6;
  margin: 0 0 16px 0;
`;

const DecoSlideMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const DecoSlideOrder = styled.span`
  font-size: 0.85rem;
  color: #00ffc8;
  font-weight: 600;
  background: rgba(0, 255, 200, 0.1);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 255, 200, 0.3);
`;

const DecoSlideActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const DecoImagePreview = styled.div`
  border: 2px dashed rgba(0, 255, 200, 0.3);
  border-radius: 12px;
  padding: 12px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
`;

// Catering Cards Styled Components
const CateringCardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
`;

const CateringCardItem = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(0, 255, 200, 0.05), rgba(0, 163, 255, 0.05));
    opacity: 0;
    transition: opacity 0.5s;
    pointer-events: none;
  }
  
  &:hover {
    box-shadow: 0 16px 48px rgba(0, 255, 200, 0.15);
    transform: translateY(-8px);
    border-color: rgba(0, 255, 200, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
`;

const CateringCardHeader = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
`;

const CateringCardName = styled.h3`
  font-size: 1.2rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 12px 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
`;

const CateringCardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CateringCardOrder = styled.span`
  font-size: 0.85rem;
  color: #00ffc8;
  font-weight: 600;
  background: rgba(0, 255, 200, 0.1);
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(0, 255, 200, 0.3);
`;

const CateringCardBody = styled.div`
  padding: 20px;
  background: rgba(0, 0, 0, 0.2);
`;

const CateringCardDescription = styled.p`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 16px 0;
  font-style: italic;
  line-height: 1.6;
`;

const CateringCardTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 12px 0;
`;

const CateringCardSubtitle = styled.p`
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const CateringCardActions = styled.div`
  padding: 16px 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
`;

export default HomePageManagement;

